import { register, getHandler } from './index';
import { getInstanceDefinition } from '../cardLookup';
import {
  revealTopOfOwnDeck, grantGold, grantPhysicalAttack, grantMagicAttack, grantXp, grantLight,
  destroyFromHand, discardFromHand, drawCards, moveHallCard, isHeroLike, queueHandChoice, gainCurse, grantBuys,
  pickRandomInstanceId, lookAtTopOfDeck, getEquippedWeaponIds, getWeaponCapacity, queuePreparePick, setAsideForGame,
  becomeDuplicateOf, destroyFromDiscard, findAllBuyableSources, grantTemporaryStrength,
} from '../effectHelpers';
import { performLevelUp, getRegularLevelUpCandidates } from '../../village/leveling';
import { hasDarknessAtRank, computeTotalLight } from '../../combat/lightDarkness';
import { compactAndRefill } from '../../dungeon/hall';
import { performBuy } from '../../actions/villageActions';
import { buyOneCard } from '../buyOneCard';
import { markMonster } from '../../actions/dungeonActions';

function updatePlayer(state, playerId, updateFn) {
  return { ...state, players: state.players.map((p) => (p.id === playerId ? updateFn(p) : p)) };
}

// --- Aird ---------------------------------------------------------------

// L1/L2: "All players discard the top card of their deck. Gain the gold
// value of one discarded card [and, at L2, Physical Attack equal to it]."
function airdDiscardTop(state, playerId, grantAttack) {
  let nextState = state;
  const discarded = [];
  for (const p of nextState.players) {
    const { state: afterReveal, instanceId } = revealTopOfOwnDeck(nextState, p.id);
    nextState = afterReveal;
    if (instanceId) discarded.push(instanceId);
  }
  if (!discarded.length) return nextState;
  const best = discarded
    .map((id) => ({ id, gold: getInstanceDefinition(nextState, id).goldValue || 0 }))
    .sort((a, b) => b.gold - a.gold)[0];
  nextState = grantGold(nextState, playerId, best.gold);
  if (grantAttack) nextState = grantPhysicalAttack(nextState, playerId, best.gold);
  return nextState;
}

register('hero-aird-l1', { village: [{ resolve: (state, { playerId }) => airdDiscardTop(state, playerId, false) }] });
register('hero-aird-l2', { dungeon: [{ resolve: (state, { playerId }) => airdDiscardTop(state, playerId, true) }] });

register('hero-aird-l3', {
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const others = state.players.filter((p) => p.id !== playerId);
      const candidates = others.flatMap((p) => p.hand
        .filter((id) => isHeroLike(getInstanceDefinition(state, id)))
        .map((id) => ({ instanceId: id, ownerId: p.id })));
      const picked = choices?.instanceId
        ? candidates.find((c) => c.instanceId === choices.instanceId)
        : candidates[0];
      if (!picked) throw new Error('No opposing hero available to borrow.');
      let nextState = updatePlayer(state, picked.ownerId, (p) => ({
        ...p, hand: p.hand.filter((id) => id !== picked.instanceId),
      }));
      nextState = updatePlayer(nextState, playerId, (p) => ({
        ...p,
        hand: [...p.hand, picked.instanceId],
        turnFlags: { ...p.turnFlags, pendingHeroReturns: [...p.turnFlags.pendingHeroReturns, picked] },
      }));
      return nextState;
    },
  }],
});

// --- Bhoidwood ------------------------------------------------------------

register('hero-bhoidwood-l2', {
  dungeon: [{
    resolve: (state, { choices }) => {
      const a = choices?.rankA ?? 0;
      const b = choices?.rankB ?? 1;
      if (Math.abs(a - b) !== 1) throw new Error('Must switch two adjacent ranks.');
      return moveHallCard(state, a, b);
    },
  }],
});

register('hero-bhoidwood-l3', {
  // "Rearrange the hall" - full permutation via choices.newOrder (array of
  // instanceId|null in the desired rank order). No-ops without a UI-supplied
  // order rather than guessing an arrangement on the player's behalf.
  dungeon: [{
    resolve: (state, { choices }) => {
      if (!choices?.newOrder) return state;
      if (choices.newOrder.length !== state.dungeonHall.length) throw new Error('New hall order must cover every rank.');
      return { ...state, dungeonHall: [...choices.newOrder] };
    },
  }],
});

// --- Caliginite -------------------------------------------------------------

register('hero-caliginite-l3', {
  // "Add Physical Attack +6 if there is Darkness." Dungeon abilities resolve
  // before a monster is chosen, so the real Darkness check (which needs a
  // rank) isn't knowable unless the caller already knows which monster
  // they're about to fight and says so via choices.hallRankIndex - the same
  // "supply a fully-resolved choice, don't pause mid-resolution" convention
  // used elsewhere (e.g. veilminderCancel). Without a declared rank, falls
  // back to a conservative proxy: zero Light present means Darkness is
  // unavoidable at any rank; any Light at all means it's NOT guaranteed
  // (see hasDarknessAtRank - some Light doesn't imply no Darkness), so the
  // ability is refused rather than silently under/over-granting the bonus.
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      if (choices?.hallRankIndex !== undefined) {
        if (!hasDarknessAtRank(state, playerId, choices.hallRankIndex)) {
          throw new Error("Caliginite Silencer's bonus only applies if there is Darkness at the chosen rank.");
        }
        return grantPhysicalAttack(state, playerId, 6);
      }
      const player = state.players.find((p) => p.id === playerId);
      const hasLight = player.hand.some((id) => {
        const def = getInstanceDefinition(state, id);
        return (def.light || 0) > 0;
      });
      if (hasLight) throw new Error("Caliginite Silencer's bonus isn't guaranteed with Light present - declare choices.hallRankIndex to check a specific rank.");
      return grantPhysicalAttack(state, playerId, 6);
    },
    // Mirrors resolve's own no-declared-rank fallback (the only form
    // "is this usable at all" can check without a specific fight already
    // chosen) - see the block comment above for why zero Light is the
    // conservative proxy for "Darkness guaranteed."
    isUsable: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      return !player.hand.some((id) => (getInstanceDefinition(state, id).light || 0) > 0);
    },
  }],
});

// --- Deepstrider ------------------------------------------------------------

function deepstriderReveal(count) {
  return (state, { playerId, choices }) => {
    let nextState = state;
    const dungeonDeck = [...nextState.dungeonDeck];
    const revealed = dungeonDeck.splice(0, Math.min(count, dungeonDeck.length));
    nextState = { ...nextState, dungeonDeck };
    nextState = updatePlayer(nextState, playerId, (p) => ({
      ...p,
      turnFlags: {
        ...p.turnFlags,
        groupConditionalAttackBonuses: [
          ...p.turnFlags.groupConditionalAttackBonuses,
          ...revealed.map((id) => ({ group: getInstanceDefinition(nextState, id).group, amount: 1, type: 'physical' })),
        ],
      },
    }));
    // Place the revealed cards back on top and/or bottom of the dungeon deck
    // (default: all back on top, preserving their order) unless the caller
    // specifies where each one goes.
    const placement = choices?.placement || revealed.map(() => 'top');
    const toTop = [];
    const toBottom = [];
    revealed.forEach((id, i) => ((placement[i] === 'bottom' ? toBottom : toTop).push(id)));
    nextState = { ...nextState, dungeonDeck: [...toTop, ...nextState.dungeonDeck, ...toBottom] };
    return nextState;
  };
}

register('hero-deepstrider-l2', { dungeon: [{ resolve: deepstriderReveal(1) }] });
register('hero-deepstrider-l3', { dungeon: [{ resolve: deepstriderReveal(2) }] });

// --- Drua -------------------------------------------------------------------
// "Destroy 1 card in your hand." / "Destroy 1 [card with the Disease trait -
// see below] to draw 1 [or 2, at L3] card(s)." Curses in this edition carry
// the Disease trait (confirmed on every ToR curse card's own trait line), so
// "disease" targeting here means "a card with the Disease trait" generically.
function druaDestroyAnyCandidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => id !== instanceId);
}

function druaDestroyAny(state, { playerId, instanceId, choices }) {
  const targetId = choices?.instanceId ?? druaDestroyAnyCandidates(state, { playerId, instanceId })[0];
  if (!targetId) throw new Error('No other card in hand to destroy.');
  return destroyFromHand(state, playerId, targetId);
}

function druaDiseaseCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
}

function druaDestroyDiseaseToDraw(count) {
  return (state, { playerId, choices }) => {
    const targetId = choices?.instanceId ?? druaDiseaseCandidates(state, { playerId })[0];
    if (!targetId) throw new Error('No card with the Disease trait present to destroy.');
    return drawCards(destroyFromHand(state, playerId, targetId), playerId, count);
  };
}

register('hero-drua-l1', { dungeon: [{ resolve: druaDestroyAny, getCandidates: druaDestroyAnyCandidates }] });
register('hero-drua-l2', {
  village: [
    { resolve: druaDestroyAny, getCandidates: druaDestroyAnyCandidates },
    { resolve: druaDestroyDiseaseToDraw(1), getCandidates: druaDiseaseCandidates },
  ],
  dungeon: [
    { resolve: druaDestroyAny, getCandidates: druaDestroyAnyCandidates },
    { resolve: druaDestroyDiseaseToDraw(1), getCandidates: druaDiseaseCandidates },
  ],
});
register('hero-drua-l3', {
  village: [
    { resolve: druaDestroyAny, getCandidates: druaDestroyAnyCandidates },
    { resolve: druaDestroyDiseaseToDraw(2), getCandidates: druaDiseaseCandidates },
  ],
  dungeon: [
    { resolve: druaDestroyAny, getCandidates: druaDestroyAnyCandidates },
    { resolve: druaDestroyDiseaseToDraw(2), getCandidates: druaDiseaseCandidates },
  ],
});

// --- Glamercast ---------------------------------------------------------

register('hero-glamercast-l3', {
  spoils: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroCount = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id))).length;
      return grantXp(state, playerId, heroCount);
    },
  }],
});

// --- Thundermage ----------------------------------------------------------

register('hero-thundermage-l3', {
  dungeon: [{
    resolve: (state, { playerId }) => {
      const monsterInstanceId = state.dungeonHall[0];
      if (!monsterInstanceId) throw new Error('No monster at rank 1.');
      const dungeonHall = [...state.dungeonHall];
      dungeonHall[0] = null;
      let nextState = updatePlayer({ ...state, dungeonHall }, playerId, (p) => ({
        ...p, discard: [...p.discard, monsterInstanceId],
      }));
      // Matt reported this directly: "After the thunder mage level three
      // took the monster in rank one it didn't refill the hall which it
      // should've done." Removing a monster from the hall always refills
      // from the dungeon deck (resolveCombat.js does this after every
      // fight) - this handler used to leave a bare null slot instead,
      // since it built the hall directly rather than going through the
      // shared compactAndRefill helper. Also fires Raid/Breach on whatever
      // gets revealed, same as a normal refill would.
      nextState = compactAndRefill(nextState, { playerId });
      // "End your turn" - same self-enforcing mechanism as King Caelan's
      // Writ (village-king-caelans-writ): the card's own text overrides the
      // default step order, so useHandCardAbility auto-dispatches endTurn
      // rather than leaving it up to whichever caller happens to remember.
      // If the refill above just queued a mandatory Raid destroy-choice,
      // useHandCardAbility defers the actual endTurn call until that's
      // resolved (see handChoiceActions.js's dropDeadEntries) rather than
      // ending the turn out from under an unresolved choice.
      return { ...nextState, meta: { ...nextState.meta, endTurnOverride: true } };
    },
    isUsable: (state) => Boolean(state.dungeonHall[0]),
  }],
});

// --- Veilminder -------------------------------------------------------------

function veilminderCancel(destroyOrDiscard) {
  return (state, { playerId, instanceId, choices }) => {
    const targetRank = choices?.hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
    if (targetRank === -1 || !state.dungeonHall[targetRank]) throw new Error('No monster present to target.');
    const monsterInstanceId = state.dungeonHall[targetRank];
    const which = choices?.effectType === 'aftermath' ? 'cancelledAftermathInstanceIds' : 'cancelledBattleInstanceIds';
    let nextState = updatePlayer(state, playerId, (p) => ({
      ...p, turnFlags: { ...p.turnFlags, [which]: [...p.turnFlags[which], monsterInstanceId] },
    }));
    nextState = destroyOrDiscard(nextState, playerId, instanceId);
    return nextState;
  };
}

// Usable only when some hall monster actually prints a Battle or Aftermath
// line - a hall of effect-less monsters gives the cancel nothing to land
// on, and (village-blood-debt's isUsable precedent) an unusable card
// shouldn't feed the end-turn "unused ability" nag either.
const veilminderIsUsable = (state) => state.dungeonHall.some((id) => {
  if (!id) return false;
  const def = getInstanceDefinition(state, id);
  return (def.abilityText.battle || []).length > 0 || (def.abilityText.aftermath || []).length > 0;
});

register('hero-veilminder-l1', { dungeon: [{ resolve: veilminderCancel(destroyFromHand), isUsable: veilminderIsUsable }] });
register('hero-veilminder-l2', { dungeon: [{ resolve: veilminderCancel(discardFromHand), isUsable: veilminderIsUsable }] });
register('hero-veilminder-l3', {
  dungeon: [{
    resolve: (state, { playerId, instanceId }) => {
      let nextState = updatePlayer(state, playerId, (p) => ({
        ...p, turnFlags: { ...p.turnFlags, allBattleAftermathCancelled: true },
      }));
      nextState = discardFromHand(nextState, playerId, instanceId);
      return drawCards(nextState, playerId, 1);
    },
  }],
});

// --- Whetmage -----------------------------------------------------------

// Matt reported this directly: "I just used the ability on the Whetmage
// Honer, the dungeon ability which lets you level up another hero... but
// what it actually did was it leveled up itself and became a Whetmage
// Finisher, which is definitely not allowed." The card's own printed text
// says "another level 1 hero" - this claimed to already exclude Whetmage
// itself (see the old comment below), but never actually did: unlike
// whetmageAnyHeroCandidates (which correctly filters `id === instanceId`),
// this never received/checked instanceId at all, so a lone Whetmage Honer
// in hand became its own only candidate and auto-targeted itself.
//
// Matt again, later (relaying a friend's report): "The Whetmage Honer and
// Finisher allows you to level up heroes as a dungeon ability... but this
// doesn't work at all in the game." Root cause: this candidate list used to
// exclude Regulars entirely (def.type === 'hero' filters them out, since a
// Regular's own type is 'basic') even though L1's own printed text
// explicitly lists "a Regular" as a valid target, and Regulars really are
// "hero cards" per the rulebook (see effectHelpers.js's isHeroLike comment)
// - a Regular just needs a SECOND choice (which level-1 hero it becomes,
// via getRegularLevelUpCandidates/targetInstanceId) on top of "which card
// to level," which this ability never asked for. Since a starting hand is
// mostly Regulars, "no other level-1 hero happens to be in hand right now"
// was the common case, not the exception - the ability wasn't broken, it
// was just never offering by far its most common legal target. Fixed by
// including Regulars in the candidate list (only when at least one level-1
// hero is actually available to become) and having PlayerDock prompt for
// the second choice - same LevelUpChoicePanel picker the standard Village
// level-up flow already uses for this exact "which level-1 hero" question.
function whetmageL1Candidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  const aLevel1HeroIsAvailable = getRegularLevelUpCandidates(state).length > 0;
  return player.hand.filter((id) => {
    if (id === instanceId) return false;
    if (player.turnFlags.leveledHeroInstanceIds.includes(id)) return false;
    const def = getInstanceDefinition(state, id);
    if (player.xp < (def.xp || 0)) return false; // not free - see below
    if (def.type === 'hero' && def.level === 1) return true;
    return def.id === 'basic-regular' && aLevel1HeroIsAvailable;
  });
}

register('hero-whetmage-l1', {
  // "Level up a Regular or another level 1 hero. Add the newly leveled hero
  // to your hand." Matt pushed back on an earlier claim here that this is
  // free: "I think the process of leveling up always requires XP by
  // default, so when it says he levels up a character that implies the
  // cost was paid." He's right - confirmed by contrast with the two cards
  // in this same pool that ARE explicitly free (Necrophidius's Trophy:
  // "...to level up a Regular or a level 1 hero WITHOUT PAYING XP";
  // Veteran Trainer's Spoils: "...to level up one hero WITHOUT PAYING XP")
  // - neither Whetmage tier's text says that. Whetmage's actual gimmick is
  // letting you level up from the Dungeon instead of needing a Village
  // visit, not waiving the XP cost - independently corroborated online (see
  // https://clevergamereference.wordpress.com/2013/04/23/thunderstone-advance-towers-of-ruin-the-heroes-part-2/:
  // "the rulebook definition of Leveling up means paying the experience
  // cost normally associated with doing so, so he does NOT waive XP
  // costs"). payXp defaults to true in performLevelUp - no override needed.
  dungeon: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const candidates = whetmageL1Candidates(state, { playerId, instanceId });
      const targetId = choices?.heroInstanceId ?? candidates[0];
      if (!targetId) throw new Error('No level 1 hero available to level up.');
      const targetDef = getInstanceDefinition(state, targetId);
      const targetInstanceId = targetDef.id === 'basic-regular' ? choices?.targetInstanceId : undefined;
      return performLevelUp(state, playerId, targetId, { destination: 'hand', targetInstanceId });
    },
    getCandidates: whetmageL1Candidates,
  }],
});

// Shared by Finisher (L2, single target) and Polisher (L3, one step of its
// own multi-target loop - see PlayerDock's useDungeonAbility) - both print
// an unrestricted "level up a hero"/"heroes" with no level-1-only
// qualifier, unlike Honer's own L1 text, so both allow any hero below level
// 3 (plus an eligible Regular, same reasoning as whetmageL1Candidates).
// Exported (unlike every other candidates fn in this file) because
// PlayerDock's useDungeonAbility needs to call it directly, standalone, to
// drive Polisher's multi-step picker loop - Polisher deliberately has no
// getCandidates registered below (see its own comment), so the generic
// registry lookup (getCandidatesFn) can't reach this one.
export function whetmageAnyHeroCandidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  const aLevel1HeroIsAvailable = getRegularLevelUpCandidates(state).length > 0;
  return player.hand.filter((id) => {
    if (id === instanceId) return false;
    if (player.turnFlags.leveledHeroInstanceIds.includes(id)) return false;
    const def = getInstanceDefinition(state, id);
    if (player.xp < (def.xp || 0)) return false; // not free - see hero-whetmage-l1's comment above
    if (def.type === 'hero' && def.level < 3) return true;
    return def.id === 'basic-regular' && aLevel1HeroIsAvailable;
  });
}

register('hero-whetmage-l2', {
  // See hero-whetmage-l1's comment above - not free, XP is paid normally.
  dungeon: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const candidates = whetmageAnyHeroCandidates(state, { playerId, instanceId });
      const targetId = choices?.heroInstanceId ?? candidates[0];
      if (!targetId) throw new Error('No other hero available to level up.');
      const targetDef = getInstanceDefinition(state, targetId);
      const targetInstanceId = targetDef.id === 'basic-regular' ? choices?.targetInstanceId : undefined;
      return performLevelUp(state, playerId, targetId, { destination: 'hand', targetInstanceId });
    },
    getCandidates: whetmageAnyHeroCandidates,
  }],
});

// Matt again (relaying a friend's report): "The Whetmage Polisher leveled
// up a random hero in my hand, but didn't let me choose who." Old behavior:
// silently walked hand order, auto-leveling every affordable eligible hero
// with zero player input at all - not "random" in the literal sense (it was
// deterministic hand order), but from the player's seat it looked exactly
// like it, and it directly contradicts "Level up ANY NUMBER of heroes,"
// which implies a real choice of which ones (0 to all), not "as many as
// possible automatically." Rebuilt as a real multi-step choice: choices.
// targets is an ORDERED array of { instanceId, targetInstanceId? }, each
// entry meaning "level this hand card, becoming this level-1 hero if it's
// a Regular" - built interactively by PlayerDock (see useDungeonAbility),
// which re-derives what's still legal to add via whetmageAnyHeroCandidates
// after each pick (a hero's own next-level copy or a Regular's target can
// be consumed by an EARLIER pick in the same batch) before letting the
// player stop or continue. This resolver just replays that already-
// validated list for real, in order - if something in it is no longer
// legal (shouldn't happen; PlayerDock validates against the same real
// engine state as it builds the list), performLevelUp throws normally
// rather than silently skipping, since every entry here was a genuine
// player choice, not a best-effort auto-pick.
register('hero-whetmage-l3', {
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const targets = choices?.targets || [];
      let nextState = state;
      for (const { instanceId: targetId, targetInstanceId } of targets) {
        nextState = performLevelUp(nextState, playerId, targetId, { destination: 'hand', targetInstanceId });
      }
      return nextState;
    },
    isUsable: (state, ctx) => whetmageAnyHeroCandidates(state, ctx).length > 0,
  }],
});

// ==========================================================================
// Caverns of Bane
// ==========================================================================

// --- Disowned -------------------------------------------------------------

function otherHeroesInHand(state, playerId, excludeInstanceId) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => id !== excludeInstanceId && isHeroLike(getInstanceDefinition(state, id)));
}

register('hero-disowned-l1', {
  // "Aftermath: Destroy 1 other hero." Rulebook glossary (Caverns of Bane
  // p.4): "The hero to be destroyed must be a card other than this card."
  aftermath: [{
    resolve: (state, { playerId, instanceId }) => queueHandChoice(state, {
      playerId,
      verb: 'destroy',
      candidateInstanceIds: otherHeroesInHand(state, playerId, instanceId),
      reason: 'Disowned Berserker: destroy 1 other hero.',
    }),
  }],
});

register('hero-disowned-l2', {
  // "Dungeon: Destroy 1 hero." Rulebook glossary: "Unlike other levels of
  // Disowned where effects and abilities only affect other heroes, a Reaver
  // can kill herself with her Dungeon ability" - deliberately includes
  // itself, unlike L1's Aftermath and this same card's own Aftermath below.
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Disowned Reaver: destroy 1 hero.' });
    },
  }],
  // "Aftermath: If equipped with a weapon, destroy 1 other hero."
  aftermath: [{
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      if (!getEquippedWeaponIds(player, instanceId).length) return state;
      return queueHandChoice(state, {
        playerId,
        verb: 'destroy',
        candidateInstanceIds: otherHeroesInHand(state, playerId, instanceId),
        reason: 'Disowned Reaver: if equipped with a weapon, destroy 1 other hero.',
      });
    },
  }],
});

register('hero-disowned-l3', {
  // "Aftermath: Destroy all other heroes. Place this card on top of your
  // deck." Mandatory/total (not a choice among several - "all"), so no
  // queueHandChoice here; loops destroyFromHand directly.
  aftermath: [{
    resolve: (state, { playerId, instanceId }) => {
      let nextState = state;
      const others = otherHeroesInHand(nextState, playerId, instanceId);
      for (const id of others) nextState = destroyFromHand(nextState, playerId, id);
      return updatePlayer(nextState, playerId, (p) => ({
        ...p, hand: p.hand.filter((id) => id !== instanceId), deck: [instanceId, ...p.deck],
      }));
    },
  }],
  // "This hero can equip two weapons." Implemented generically via
  // effectHelpers.js's getWeaponCapacity/MULTI_WEAPON_HERO_IDS - no
  // per-card handler needed here; actions/dungeonActions.js's equipWeapon
  // already allows a 2nd weapon for this specific hero id, and attackMath.js
  // sums every equipped weapon's contribution the same way it always summed
  // every card present.
});

// --- Forcemage --------------------------------------------------------------
// L1's "Add Magic Attack +1 for each spell present" is implemented in
// combat/attackMath.js's contributesTraits (spellCount) - a passive trait,
// not a Dungeon/Village ability, so no register() call for L1 itself.

// "Draw N cards. If [it is/either is] a spell, draw an additional card and
// add Magic Attack +X." Diffs hand before/after to know what was actually
// drawn (drawCards itself doesn't report drawn ids).
function forcemageDrawAndCheck(count, magicBonus) {
  return (state, { playerId }) => {
    const before = new Set(state.players.find((p) => p.id === playerId).hand);
    let nextState = drawCards(state, playerId, count);
    const drawnIds = nextState.players.find((p) => p.id === playerId).hand.filter((id) => !before.has(id));
    const drewSpell = drawnIds.some((id) => getInstanceDefinition(nextState, id).traits.includes('Spell'));
    if (drewSpell) {
      nextState = drawCards(nextState, playerId, 1);
      nextState = grantMagicAttack(nextState, playerId, magicBonus);
    }
    return nextState;
  };
}

register('hero-forcemage-l2', { dungeon: [{ resolve: forcemageDrawAndCheck(1, 1) }] });
register('hero-forcemage-l3', { dungeon: [{ resolve: forcemageDrawAndCheck(2, 2) }] });

// --- Patternmage ------------------------------------------------------------
// "Look at the top N cards of your deck. Discard M of them. Place the rest
// on top of your deck [in any order]." A reveal-from-the-TOP-OF-DECK effect
// whose default fate (unlike revealTopOfOwnDeck's Reveal-then-discard) is
// back onto the deck - see effectHelpers.js's lookAtTopOfDeck. `choices`
// resolved before dispatch (Village/Dungeon timing) makes this a real
// choice: `choices.discardInstanceIds` picks which of the revealed cards to
// discard, `choices.order` picks the resulting top-to-bottom order of
// whatever's kept. With no choices supplied, defaults to discarding the
// first `exactDiscardCount` revealed cards and keeping the rest in revealed
// order (an arbitrary-but-legal default, same convention as this file's
// other Village/Dungeon real-choice cards, e.g. Silvertongue). L3's "any or
// all" has no exact count - `exactDiscardCount` left undefined means
// `choices.discardInstanceIds` is used as-is (default: discard none, an
// always-legal reading of "any or all").
//
// L2/L3's separate "Draw 1 card." Dungeon line is registered as its own
// independent ability entry (not bundled into the same resolve) - the
// source data doesn't make clear whether the two are meant to be used
// together or are genuinely 2 unrelated choices, so this follows the same
// independent-multi-ability shape as Drua rather than guessing they're
// linked.
function patternmageLook(count, { exactDiscardCount } = {}) {
  return (state, { playerId, choices }) => {
    const { state: afterLook, instanceIds: revealed } = lookAtTopOfDeck(state, playerId, count);
    if (!revealed.length) return afterLook;
    const requested = (choices?.discardInstanceIds || []).filter((id) => revealed.includes(id));
    const discardIds = exactDiscardCount !== undefined && requested.length !== exactDiscardCount
      ? revealed.slice(0, exactDiscardCount)
      : requested;
    const keepIds = revealed.filter((id) => !discardIds.includes(id));
    const requestedOrder = (choices?.order || []).filter((id) => keepIds.includes(id));
    const orderedKeepIds = [...requestedOrder, ...keepIds.filter((id) => !requestedOrder.includes(id))];
    return updatePlayer(afterLook, playerId, (p) => ({
      ...p,
      discard: [...p.discard, ...discardIds],
      deck: [...orderedKeepIds, ...p.deck],
    }));
  };
}

register('hero-patternmage-l1', {
  // "Look at the top 2 cards of your deck. Discard 1 of them. Place the other on top of your deck."
  village: [{ resolve: patternmageLook(2, { exactDiscardCount: 1 }) }],
  dungeon: [{ resolve: patternmageLook(2, { exactDiscardCount: 1 }) }],
});

register('hero-patternmage-l2', {
  // "Look at the top 3 cards of your deck. Discard 2 of them. Place the other on top of your deck."
  village: [{ resolve: patternmageLook(3, { exactDiscardCount: 2 }) }],
  dungeon: [
    { resolve: patternmageLook(3, { exactDiscardCount: 2 }) },
    { resolve: (state, { playerId }) => drawCards(state, playerId, 1) },
  ],
});

register('hero-patternmage-l3', {
  // "Look at the top 3 cards of your deck. Discard any or all of them. Place the rest on top of your deck in any order."
  village: [{ resolve: patternmageLook(3) }],
  dungeon: [
    { resolve: patternmageLook(3) },
    { resolve: (state, { playerId }) => drawCards(state, playerId, 1) },
  ],
});
// Patternmage Ordainer's (L3) alwaysActive line was a literal "ability"
// placeholder in the source data (see buildCardData.mjs's
// SET_TAG_CORRECTIONS-adjacent comment) - its real passive text is unknown,
// so it's not implemented here; a separate, narrower gap from the lines
// above (all of which are now implemented).

// --- Rapparee ---------------------------------------------------------------

register('hero-rapparee-l2', {
  // "Spoils: Buy 1 item or weapon." Free, like every other ToR Spoils-buy
  // (Bounty Hunter's own "Buy 1 village card" precedent).
  spoils: [buyOneCard({
    matches: (def) => (def.traits || []).includes('Item') || (def.traits || []).includes('Weapon'),
    errorMessage: 'No item or weapon remains to buy.',
    includeBasics: false,
  })],
});

register('hero-rapparee-l3', {
  // "Spoils: Buy 1 village card. Place it on top of your deck."
  spoils: [buyOneCard({
    errorMessage: 'No village cards remain to buy.',
    includeBasics: false,
    buyOptions: { requireGoldCheck: false, destination: 'deck' },
  })],
});

// --- Skinshifter --------------------------------------------------------------
// "Discard [or destroy] a card to add Physical Attack +X and Strength +Y."
// No explicit "1 hero"/"another hero" target named (contrast Thunderstone
// Shard's own "ONE hero gains Strength +2", which does name a target) - read
// as Skinshifter buffing itself (Matt confirmed: "self targeting is right").
function skinshifterBonus(verb, physicalBonus, strengthBonus, { repeatOnDiseaseOnce = false } = {}) {
  return (state, { playerId, instanceId, choices }) => {
    const player = state.players.find((p) => p.id === playerId);
    const candidates = player.hand.filter((id) => id !== instanceId);
    const targetId = choices?.heroInstanceId ?? candidates[0];
    if (!targetId) return state;
    // Matt: "diseases and curses are interchangeable, they are two words
    // for the same thing" - traits.includes('Disease') is the same check
    // used everywhere else in this file for "was the targeted card a curse".
    const wasDisease = getInstanceDefinition(state, targetId).traits.includes('Disease');
    let nextState = verb === 'destroy' ? destroyFromHand(state, playerId, targetId) : discardFromHand(state, playerId, targetId);
    nextState = grantPhysicalAttack(nextState, playerId, physicalBonus);
    nextState = grantTemporaryStrength(nextState, playerId, instanceId, strengthBonus);
    // Skinshifter Terror (L3): "If the card was a disease, you may use this
    // ability a second time this turn." A ONE-TIME bonus repeat (the card
    // says "a second time," not "keep repeating") - a dedicated
    // usedAbilityInstanceIds entry (distinct from the normal per-line key)
    // remembers the bonus has already been spent this turn, so a 2nd
    // disease-destroy this same turn doesn't grant a 3rd use.
    if (repeatOnDiseaseOnce && wasDisease) {
      const repeatKey = `${instanceId}:dungeon:0:skinshifter-repeat`;
      if (!player.turnFlags.usedAbilityInstanceIds.includes(repeatKey)) {
        nextState = {
          ...nextState,
          meta: { ...nextState.meta, bypassAbilityUsageCap: true },
          players: nextState.players.map((p) => (p.id === playerId
            ? { ...p, turnFlags: { ...p.turnFlags, usedAbilityInstanceIds: [...p.turnFlags.usedAbilityInstanceIds, repeatKey] } }
            : p)),
        };
      }
    }
    return nextState;
  };
}

// Brian (bug report, 2026-08-15): "when used it doesn't allow me to choose
// which card is discarded as it should." Same missing-getCandidates gap as
// the destroyChoicePickers.test.js batch - the resolver above always took
// choices.heroInstanceId, but without a registered getCandidates,
// PlayerDock's resolveChoicesFor never shows the picker and the
// candidates[0] fallback silently eats the first card in hand. The legal
// target set ("any other card in your hand") is identical to Drua's, so
// this reuses its candidates fn rather than redefining it.
register('hero-skinshifter-l1', { dungeon: [{ resolve: skinshifterBonus('discard', 2, 1), getCandidates: druaDestroyAnyCandidates }] });
register('hero-skinshifter-l2', { dungeon: [{ resolve: skinshifterBonus('discard', 3, 2), getCandidates: druaDestroyAnyCandidates }] });
register('hero-skinshifter-l3', { dungeon: [{ resolve: skinshifterBonus('destroy', 4, 3, { repeatOnDiseaseOnce: true }), getCandidates: druaDestroyAnyCandidates }] });

// --- Spellsword ---------------------------------------------------------------
// L1/L2/L3's Magic Attack bonuses (per magic item/weapon/spell present) are
// implemented in combat/attackMath.js's contributesTraits - passive traits,
// not Dungeon/Village abilities.

register('hero-spellsword-l3', {
  // "Spoils: Buy 1 magic item, magic weapon, or spell."
  spoils: [buyOneCard({
    matches: (def) => {
      const t = def.traits || [];
      return t.includes('Spell') || (t.includes('Magic') && (t.includes('Item') || t.includes('Weapon')));
    },
    errorMessage: 'No magic item, magic weapon, or spell remains to buy.',
    includeBasics: false,
  })],
});

// --- Tuath --------------------------------------------------------------------

// "Place a monster from the hall on the bottom of the dungeon deck. Refill
// the hall[. Ignore Darkness penalties.]" Hall-rank target defaults to the
// first occupied rank if not declared - same fallback convention as
// Veilminder's own hall-targeting Dungeon abilities above (no getCandidates
// sibling for hall-rank choices anywhere yet).
function tuathBanish(ignoreDarkness) {
  return (state, { playerId, choices }) => {
    const targetRank = choices?.hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
    if (targetRank === -1 || !state.dungeonHall[targetRank]) throw new Error('No monster present to banish.');
    const monsterInstanceId = state.dungeonHall[targetRank];
    const dungeonHall = [...state.dungeonHall];
    dungeonHall[targetRank] = null;
    let nextState = compactAndRefill({ ...state, dungeonHall, dungeonDeck: [...state.dungeonDeck, monsterInstanceId] }, { playerId });
    if (ignoreDarkness) {
      nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, turnFlags: { ...p.turnFlags, ignoreDarknessPenalty: true } }));
    }
    return nextState;
  };
}

const tuathIsUsable = (state) => state.dungeonHall.some(Boolean);

register('hero-tuath-l2', { dungeon: [{ resolve: tuathBanish(false), isUsable: tuathIsUsable }] });
register('hero-tuath-l3', { dungeon: [{ resolve: tuathBanish(true), isUsable: tuathIsUsable }] });

// ==========================================================================
// Root of Corruption
// ==========================================================================

// --- Armsman ------------------------------------------------------------

register('hero-armsman-l2', {
  // "Dungeon: Buy 1 weapon and place it into your hand." "Buy" (unlike
  // "Place"/"Gain") costs gold per the rulebook's own Buy/Gain distinction -
  // this is a genuine mid-turn purchase from the shared hand-gold pool, not
  // a free grant like Rapparee's Spoils-timing "Buy" abilities (Spoils
  // itself is inherently a post-combat reward, independent of the word
  // "buy" used loosely in its text).
  dungeon: [buyOneCard({
    matches: (def) => def.traits.includes('Weapon'),
    errorMessage: 'No weapon remains to buy.',
    includeBasics: false,
    buyOptions: { destination: 'hand' },
  })],
  // "Aftermath: Destroy 1 weapon."
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Armsman Stronghold: destroy 1 weapon.' });
    },
  }],
});

register('hero-armsman-l3', {
  // "Dungeon: Place 1 weapon from a village stack into your hand." Free
  // ("Place," not "Buy").
  dungeon: [buyOneCard({
    matches: (def) => def.traits.includes('Weapon'),
    errorMessage: 'No weapon remains in the village.',
    includeBasics: false,
    buyOptions: { requireGoldCheck: false, destination: 'hand' },
  })],
  // "Spoils: Destroy 1 weapon."
  spoils: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Armsman Citadel: destroy 1 weapon.' });
    },
  }],
});

// --- Blademage ----------------------------------------------------------
// "One spell becomes a Weight 0 magic weapon equipped to this hero (it is
// still a spell) that adds Magic Attack +3." / L3: "Each spell becomes a
// Weight 0 magic weapon (each is still a spell) that adds Magic Attack
// equal to its gold cost." Matt, confirming directly: this DOES occupy the
// hero's normal weapon-equip slot (heroes cap at 1 - effectHelpers.js's
// getWeaponCapacity/getEquippedWeaponIds), not just an independent flat
// bonus - so the chosen spell instance(s) are written straight into
// player.equippedWeapons, the SAME structure real weapons use, rather than
// calling dungeonActions.js's own equipWeapon action (which explicitly
// throws for a non-Weapon-trait card). Reuses everything else generically,
// for free: PlayerDock.vue's handGroups already nests anything present in
// equippedWeapons under its hero regardless of trait; the house-rule
// UNEQUIP_WEAPON action already operates on equippedWeapons membership with
// no Weapon-trait check of its own; effectHelpers.js's
// unequipIfEquippedWeapon (destroyFromHand/discardFromHand's own cleanup)
// already handles ANY instanceId leaving hand, not just Weapon-trait ones.
// "it is still a spell" - the spell's own `traits` are never touched, so
// every trait-based check elsewhere (Doomladen-presence, "destroy 1
// spell", etc.) still sees it correctly; it's simply ALSO tracked as this
// hero's equipped weapon on top of that.
function blademageSpellCandidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  const heroDef = getInstanceDefinition(state, instanceId);
  if (getEquippedWeaponIds(player, instanceId).length >= getWeaponCapacity(heroDef)) return []; // weapon slot already full
  return player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Spell'));
}

register('hero-blademage-l2', {
  dungeon: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const candidates = blademageSpellCandidates(state, { playerId, instanceId });
      const spellId = choices?.instanceId ?? candidates[0];
      if (!spellId) return state; // no spell present, or the weapon slot is already occupied
      const player = state.players.find((p) => p.id === playerId);
      const equipped = getEquippedWeaponIds(player, instanceId);
      const nextState = updatePlayer(state, playerId, (p) => ({
        ...p, equippedWeapons: { ...p.equippedWeapons, [instanceId]: [...equipped, spellId] },
      }));
      return grantMagicAttack(nextState, playerId, 3);
    },
    getCandidates: blademageSpellCandidates,
  }],
});

register('hero-blademage-l3', {
  // "Each spell becomes a Weight 0 magic weapon... that adds Magic Attack
  // equal to its gold cost." Unlike L2's single pick, this printed "EACH"
  // is a deliberate exception to the normal 1-weapon cap (same shape as
  // Disowned Bloodrager's own explicit 2-weapon exception) - every spell
  // currently in hand becomes equipped simultaneously, uncapped, each
  // contributing its own gold cost to the total Magic Attack granted.
  dungeon: [{
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const alreadyEquipped = getEquippedWeaponIds(player, instanceId);
      const spellIds = player.hand.filter((id) => !alreadyEquipped.includes(id) && getInstanceDefinition(state, id).traits.includes('Spell'));
      if (!spellIds.length) return state;
      // "gold COST" is the spell's price in the village, not its goldValue
      // (the gold it produces, 0 on nearly every spell). Bug report
      // -P2GZ06vfmKA2EzTTUM6 (Matt): Bolt of Purity (cost 6) added nothing.
      const total = spellIds.reduce((sum, id) => sum + (getInstanceDefinition(state, id).cost || 0), 0);
      const nextState = updatePlayer(state, playerId, (p) => ({
        ...p, equippedWeapons: { ...p.equippedWeapons, [instanceId]: [...alreadyEquipped, ...spellIds] },
      }));
      return total ? grantMagicAttack(nextState, playerId, total) : nextState;
    },
  }],
});

// --- Honormain ------------------------------------------------------------

// "Discard 1 card or destroy 1 disease." Discarding a disease is always
// strictly worse than destroying it (destroyFromHand permanently cures it
// back to the bottom of the shared curse deck; discardFromHand just leaves
// it sitting in this player's own discard, to be redrawn later) - so the
// real decision space collapses to one flat pick: choose ANY card in hand,
// and the verb follows automatically from whether that specific card
// happens to be a disease. This still preserves genuine choice where it
// actually matters (e.g. preferring to discard a dead card and leave a
// disease in hand for a later dedicated "destroy 1 curse" effect, rather
// than being forced to cure whatever disease is present right now) without
// needing a separate verb-then-target two-step flow. Matt reported this
// gap directly via docs/expansion-audit.md's review list: "no explicit
// player choice mechanism between two different VERBS."
function honormainCandidates(state, { playerId }) {
  return state.players.find((p) => p.id === playerId).hand;
}
function honormainDiscardOrCureDisease(state, { playerId, choices }) {
  const player = state.players.find((p) => p.id === playerId);
  const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
  // Default (no explicit choice) unchanged from before this fix: prefer
  // curing a present disease over an arbitrary discard.
  const targetId = choices?.instanceId ?? diseaseId ?? player.hand[0];
  if (!targetId) return state;
  const isDisease = getInstanceDefinition(state, targetId).traits.includes('Disease');
  return isDisease ? destroyFromHand(state, playerId, targetId) : discardFromHand(state, playerId, targetId);
}

register('hero-honormain-l1', {
  village: [{ resolve: honormainDiscardOrCureDisease, getCandidates: honormainCandidates }],
  dungeon: [{ resolve: honormainDiscardOrCureDisease, getCandidates: honormainCandidates }],
});

// L2/L3: "Discard 1 card to add Magic Attack +1." / "Destroy 1 disease to
// add Magic Attack +2." - two independently-usable lines (not a choice
// between them, unlike L1 above), each with a fixed verb - but both still
// lacked a real WHICH-card choice (previously always player.hand[0], or
// the first disease found via .find()). getCandidates fixes that the same
// way as every other real-choice ability in this file.
function honormainDiscardForMagicCandidates(state, { playerId }) {
  return state.players.find((p) => p.id === playerId).hand;
}
function honormainDiscardForMagic(state, { playerId, choices }) {
  const player = state.players.find((p) => p.id === playerId);
  const targetId = choices?.instanceId ?? player.hand[0];
  if (!targetId) return state;
  return grantMagicAttack(discardFromHand(state, playerId, targetId), playerId, 1);
}
function honormainCureForMagicCandidates(state, { playerId }) {
  return state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
}
function honormainCureForMagic(state, { playerId, choices }) {
  const candidates = honormainCureForMagicCandidates(state, { playerId });
  const targetId = choices?.instanceId ?? candidates[0];
  if (!targetId) return state;
  return grantMagicAttack(destroyFromHand(state, playerId, targetId), playerId, 2);
}

register('hero-honormain-l2', {
  dungeon: [
    { resolve: honormainDiscardForMagic, getCandidates: honormainDiscardForMagicCandidates },
    { resolve: honormainCureForMagic, getCandidates: honormainCureForMagicCandidates },
  ],
});

register('hero-honormain-l3', {
  // Same two lines as L2, marked repeatable in source data (repeatDungeons) -
  // repeat-usage enforcement is handled generically wherever `repeatable` is
  // already respected (see FIELD_TO_BUCKET's repeatDungeon(s) mapping).
  dungeon: [
    { resolve: honormainDiscardForMagic, getCandidates: honormainDiscardForMagicCandidates },
    { resolve: honormainCureForMagic, getCandidates: honormainCureForMagicCandidates },
  ],
});

// --- Moonclaw -------------------------------------------------------------

register('hero-moonclaw-l1', {
  // "Aftermath: If you did not defeat a monster, destroy this card."
  aftermath: [{
    resolve: (state, { playerId, instanceId, won }) => (won ? state : destroyFromHand(state, playerId, instanceId)),
  }],
});

// "Reveal the bottom card of the dungeon deck. If it is a non-Guardian
// monster, you may switch it with a monster in rank [minRankIndex+1 or
// higher / anywhere in the hall]." "You may" defaults to accepting the
// swap (there's no real downside) rather than a genuine decline option,
// same convention as other "you may" abilities elsewhere.
function moonclawSwap(minRankIndex) {
  return (state) => {
    if (!state.dungeonDeck.length) return state;
    const bottomId = state.dungeonDeck[state.dungeonDeck.length - 1];
    const bottomDef = getInstanceDefinition(state, bottomId);
    if (bottomDef.type !== 'monster' || bottomDef.traits.includes('Guardian')) return state;
    const candidateRanks = state.dungeonHall
      .map((id, i) => i)
      .filter((i) => state.dungeonHall[i] && (minRankIndex === null || i >= minRankIndex));
    if (!candidateRanks.length) return state;
    const targetRank = candidateRanks[0];
    const targetId = state.dungeonHall[targetRank];
    const dungeonDeck = [...state.dungeonDeck];
    dungeonDeck[dungeonDeck.length - 1] = targetId;
    const dungeonHall = [...state.dungeonHall];
    dungeonHall[targetRank] = bottomId;
    return { ...state, dungeonDeck, dungeonHall };
  };
}

register('hero-moonclaw-l2', { dungeon: [{ resolve: moonclawSwap(2) }] }); // rank 3 = index 2
register('hero-moonclaw-l3', { dungeon: [{ resolve: moonclawSwap(null) }] }); // any rank in the hall

// --- Profaned ---------------------------------------------------------------

register('hero-profaned-l1', {
  // "Light +1. Gain 1 curse."
  dungeon: [{ resolve: (state, { playerId }) => gainCurse(grantLight(state, playerId, 1), playerId) }],
});

function profanedDrawAndCurse(state, { playerId }) {
  return gainCurse(drawCards(state, playerId, 1), playerId);
}
function profanedCureForMagic(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
  if (!diseaseId) return state;
  return grantMagicAttack(destroyFromHand(state, playerId, diseaseId), playerId, 3);
}

register('hero-profaned-l2', {
  // "Village/Dungeon: Draw 1 card. Gain 1 curse." plus a separate,
  // repeatable Dungeon line: "Destroy 1 disease to add Magic Attack +3."
  // (source data's dungeons/repeatDungeons both normalize into the same
  // 'dungeon' bucket, in that field-declaration order - see
  // buildCardData.mjs's FIELD_TO_BUCKET - so this is dungeon:0/dungeon:1.)
  village: [{ resolve: profanedDrawAndCurse }],
  dungeon: [{ resolve: profanedDrawAndCurse }, { resolve: profanedCureForMagic }],
});
register('hero-profaned-l3', {
  // "repeatDungeons: Destroy 1 disease to add Magic Attack +3 and draw 1 card."
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
      if (!diseaseId) return state;
      return drawCards(grantMagicAttack(destroyFromHand(state, playerId, diseaseId), playerId, 3), playerId, 1);
    },
  }],
});

// --- Silvertongue ---------------------------------------------------------
// Rulebook glossary (TS9_Rulebook_04Jun.pdf p.8): "Village cards are any
// card with a green border. Giving a card away is different than discarding
// or destroying it." - "give" moves the card to another player's discard
// pile, not out of the game. Matt, confirming directly: Basic cards (Torch,
// Regular, Longspear, Thunderstone Shard - engine type "basic", to
// distinguish the always-available supply from the randomized village-row
// offer) have that same green border, so they count as "village cards" too
// for this and every other "village card" effect (see Cyclopic Horror/
// Legion Sorcerer in monsters.js for the other 2 - all 3 shared this exact
// gap, previously excluding Basics).
function silvertongueCandidates(state, playerId) {
  return state.players.find((p) => p.id === playerId).hand
    .filter((id) => ['village', 'basic'].includes(getInstanceDefinition(state, id).type));
}

register('hero-silvertongue-l1', {
  // "Place a village card present back on its stack to gain 2 gold."
  village: [{
    resolve: (state, { playerId, choices }) => {
      const candidates = silvertongueCandidates(state, playerId);
      const targetId = choices?.instanceId ?? candidates[0];
      if (!targetId) return state;
      const targetDef = getInstanceDefinition(state, targetId);
      const key = targetDef.id;
      // A Basic card (now a valid target - see silvertongueCandidates' own
      // comment) belongs back on basicStacks, not villageStacks, or it'd
      // silently vanish from both board sections while still technically
      // "in the game."
      const stackField = targetDef.type === 'basic' ? 'basicStacks' : 'villageStacks';
      let nextState = updatePlayer(state, playerId, (p) => ({ ...p, hand: p.hand.filter((id) => id !== targetId) }));
      nextState = {
        ...nextState,
        village: { ...nextState.village, [stackField]: { ...nextState.village[stackField], [key]: [targetId, ...(nextState.village[stackField][key] || [])] } },
      };
      return grantGold(nextState, playerId, 2);
    },
  }],
});

function silvertongueGive(state, { playerId, choices }) {
  const candidates = silvertongueCandidates(state, playerId);
  const targetId = choices?.instanceId ?? candidates[0];
  if (!targetId) return null;
  const otherIds = state.players.map((p) => p.id).filter((id) => id !== playerId);
  const targetPlayerId = choices?.targetPlayerId && otherIds.includes(choices.targetPlayerId) ? choices.targetPlayerId : otherIds[0];
  if (!targetPlayerId) return null;
  let nextState = updatePlayer(state, playerId, (p) => ({ ...p, hand: p.hand.filter((id) => id !== targetId) }));
  nextState = updatePlayer(nextState, targetPlayerId, (p) => ({ ...p, discard: [...p.discard, targetId] }));
  return nextState;
}

function silvertongueGiveForAttackOrGold(state, { playerId, choices }) {
  const nextState = silvertongueGive(state, { playerId, choices });
  if (!nextState) return state;
  // "Give... to either add Physical Attack +2 or gain 2 gold." Village/
  // Dungeon-timing abilities get `choices` resolved before dispatch (unlike
  // Battle/Aftermath, which hardcode `choices: {}`), so this is a real
  // player choice, not a default-only approximation.
  return choices?.rewardType === 'gold' ? grantGold(nextState, playerId, 2) : grantPhysicalAttack(nextState, playerId, 2);
}

register('hero-silvertongue-l2', {
  village: [{ resolve: silvertongueGiveForAttackOrGold }],
  dungeon: [{ resolve: silvertongueGiveForAttackOrGold }],
});

function silvertongueGiveForAttackAndDraw(state, { playerId, choices }) {
  const nextState = silvertongueGive(state, { playerId, choices });
  if (!nextState) return state;
  return drawCards(grantPhysicalAttack(nextState, playerId, 3), playerId, 1);
}

register('hero-silvertongue-l3', {
  // "Give another player a village card present to add Physical Attack +3 and draw 1 card."
  village: [{ resolve: silvertongueGiveForAttackAndDraw }],
  dungeon: [{ resolve: silvertongueGiveForAttackAndDraw }],
});

// --- Woodguard ------------------------------------------------------------
// "Cannot equip a weapon." - actions/dungeonActions.js's equipWeapon (the
// only hero group with a per-card equip restriction, alongside the existing
// Strength/weight check).

register('hero-woodguard-l1', {
  // "Add Physical Attack +2. Each other player draws 1 card."
  dungeon: [{
    resolve: (state, { playerId }) => {
      let nextState = grantPhysicalAttack(state, playerId, 2);
      for (const p of nextState.players) {
        if (p.id !== playerId) nextState = drawCards(nextState, p.id, 1);
      }
      return nextState;
    },
  }],
});
register('hero-woodguard-l2', {
  // "Add Physical Attack +4. Each other player draws 1 card."
  dungeon: [{
    resolve: (state, { playerId }) => {
      let nextState = grantPhysicalAttack(state, playerId, 4);
      for (const p of nextState.players) {
        if (p.id !== playerId) nextState = drawCards(nextState, p.id, 1);
      }
      return nextState;
    },
  }],
});
register('hero-woodguard-l3', {
  dungeon: [
    {
      // "Add Physical Attack +6. Each player draws 1 card." - EVERY player,
      // including the active one this time (unlike L1/L2's "each other").
      resolve: (state, { playerId }) => {
        let nextState = grantPhysicalAttack(state, playerId, 6);
        for (const p of nextState.players) nextState = drawCards(nextState, p.id, 1);
        return nextState;
      },
    },
    {
      // "Destroy 1 disease to draw 1 card."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
        if (!diseaseId) return state;
        return drawCards(destroyFromHand(state, playerId, diseaseId), playerId, 1);
      },
    },
  ],
});

// ==========================================================================
// Numenera
// ==========================================================================

// --- Aeon -------------------------------------------------------------------

function aeonVillageStackKeys(state, maxCost) {
  return Object.keys(state.village.villageStacks).filter((k) => {
    const stack = state.village.villageStacks[k];
    return stack.length && (getInstanceDefinition(state, stack[0]).cost || 0) <= maxCost;
  });
}

register('hero-aeon-l1', {
  // "Destroy 1 card."
  village: [{
    resolve: (state, { playerId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetId = choices?.instanceId ?? player.hand[0];
      return targetId ? destroyFromHand(state, playerId, targetId) : state;
    },
    getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand,
  }],
});

function aeonTradeUp(destination) {
  return (state, { playerId, choices }) => {
    const player = state.players.find((p) => p.id === playerId);
    const destroyId = choices?.instanceId ?? player.hand[0];
    if (!destroyId) return state;
    const destroyedDef = getInstanceDefinition(state, destroyId);
    let nextState = destroyFromHand(state, playerId, destroyId);
    // "You may place 1 card of up to 2 higher cost..." - defaults to
    // accepting the trade-up when a legal target exists (same "you may"
    // convention as Moonclaw's own swap above).
    const maxCost = (destroyedDef.cost || 0) + 2;
    const keys = aeonVillageStackKeys(nextState, maxCost);
    const key = choices?.villageDefId ?? keys[0];
    if (!key) return nextState;
    return performBuy(nextState, playerId, { type: 'village', key }, { requireGoldCheck: false, destination });
  };
}

register('hero-aeon-l2', {
  // "Destroy 1 card. You may place 1 card of up to 2 higher cost from the
  // village into your discard pile." (both Village and Dungeon timings.)
  village: [{ resolve: aeonTradeUp('discard') }],
  dungeon: [{ resolve: aeonTradeUp('discard') }],
});
register('hero-aeon-l3', {
  // Same trade-up, into your HAND instead of discard.
  dungeon: [{ resolve: aeonTradeUp('hand') }],
});

// --- Charming -----------------------------------------------------------

function charmingCureForCard(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
  return diseaseId ? drawCards(destroyFromHand(state, playerId, diseaseId), playerId, 1) : state;
}

register('hero-charming-l1', {
  // "repeatVillages:0 - Destroy 1 disease to draw 1 card."
  village: [{ resolve: charmingCureForCard }],
});

function charmingPlaceFromDiscard(levelFilter) {
  return (state, { playerId, choices }) => {
    const player = state.players.find((p) => p.id === playerId);
    const candidates = player.discard.filter((id) => {
      const def = getInstanceDefinition(state, id);
      return isHeroLike(def) && levelFilter(def);
    });
    const targetId = choices?.instanceId ?? candidates[0];
    if (!targetId) return state;
    return updatePlayer(state, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== targetId), hand: [...p.hand, targetId] }));
  };
}

register('hero-charming-l2', {
  // "dungeons:0 - Place one Regular or level 1 hero from your discard pile
  // into your hand." (dungeons before repeatDungeons in FIELD_TO_BUCKET's
  // declaration order, so this is dungeon:0.)
  dungeon: [{ resolve: charmingPlaceFromDiscard((def) => (def.level ?? 1) <= 1) }, { resolve: charmingCureForCard }],
});
register('hero-charming-l3', {
  // "dungeons:0 - Place 1 hero from your discard pile into your hand." (any level.)
  dungeon: [{ resolve: charmingPlaceFromDiscard(() => true) }, { resolve: charmingCureForCard }],
});

// --- Clever -------------------------------------------------------------
// "specialEffects: This hero can only use one ability." -
// actions/abilityActions.js's SINGLE_ABILITY_PER_CARD_GROUPS (the only hero
// group with this restriction - broader than the standard per-line cap).

function cleverMonsterPresent(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const monsterId = player.hand.find((id) => getInstanceDefinition(state, id).type === 'monster');
  return monsterId ? getInstanceDefinition(state, monsterId) : null;
}

register('hero-clever-l2', {
  dungeon: [
    { resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 3) },
    {
      // "Physical Attack equal to the VP of 1 monster present. This card
      // becomes unequipped and cannot equip weapons." Monster-type cards
      // essentially never sit in hand under normal play (see
      // effectHelpers.js's isHeroLike comment on the same point) - the
      // unequip half is a no-op if it was never equipped, so only the
      // attack grant + unequip step matter when this fires at all.
      resolve: (state, { playerId, instanceId }) => {
        const monsterDef = cleverMonsterPresent(state, playerId);
        let nextState = monsterDef ? grantPhysicalAttack(state, playerId, monsterDef.points || 0) : state;
        nextState = updatePlayer(nextState, playerId, (p) => {
          const equippedWeapons = { ...p.equippedWeapons };
          delete equippedWeapons[instanceId];
          return { ...p, equippedWeapons };
        });
        return nextState;
      },
    },
  ],
});
register('hero-clever-l3', {
  dungeon: [
    { resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 5) },
    {
      resolve: (state, { playerId, instanceId }) => {
        const monsterDef = cleverMonsterPresent(state, playerId);
        let nextState = monsterDef ? grantPhysicalAttack(state, playerId, monsterDef.health || 0) : state;
        nextState = updatePlayer(nextState, playerId, (p) => {
          const equippedWeapons = { ...p.equippedWeapons };
          delete equippedWeapons[instanceId];
          return { ...p, equippedWeapons };
        });
        return nextState;
      },
    },
  ],
});

// --- Graceful -------------------------------------------------------------
// "Add Physical Attack +N while fighting a monster with a Battle [or
// Aftermath] effect." - combat/attackMath.js contributesTraits
// (monsterHasBattle/monsterHasAftermath params). "Immune to Battle and
// Aftermath effects" (L3) - the Battle half reuses effectHelpers.js's
// isShieldedByPike (extended for this card); the Aftermath half is NOT
// implemented (no shared Aftermath-targeting helper exists to extend the
// same way) - see docs/expansion-audit.md.

// --- Intelligent ----------------------------------------------------------
// "React: After a card is destroyed, add Physical Attack +2[/+4 and Light
// +2]." No react: [...] entry here - same "generic substitution, not a
// per-card effect" shape as Shield Bearer's own React (villages.js). Scoped
// to destroys during the Intelligent-holder's OWN turn (Matt, confirming
// the trigger reading) - see effectHelpers.js's offerIntelligentReact/
// INTELLIGENT_REACT_BONUS (hooked into destroyFromHand, the single choke
// point every "destroy 1 card" effect already passes through) and
// actions/handChoiceActions.js's resolveIntelligentReactOffer. L2/L3's own
// "Destroy 1 card" Dungeon ability below naturally offers this too, since
// it also goes through destroyFromHand.

function intelligentDestroy(state, { playerId, choices }) {
  const player = state.players.find((p) => p.id === playerId);
  const targetId = choices?.instanceId ?? player.hand[0];
  return targetId ? destroyFromHand(state, playerId, targetId) : state;
}
register('hero-intelligent-l2', { dungeon: [{ resolve: intelligentDestroy }] });
register('hero-intelligent-l3', { dungeon: [{ resolve: intelligentDestroy }] });

// --- Learned ----------------------------------------------------------------

function learnedDiscardToDraw(state, { playerId, choices }) {
  const player = state.players.find((p) => p.id === playerId);
  const targetId = choices?.instanceId ?? player.hand[0];
  if (!targetId) return state;
  return drawCards(discardFromHand(state, playerId, targetId), playerId, 1);
}

register('hero-learned-l1', {
  village: [{ resolve: learnedDiscardToDraw }],
  dungeon: [{ resolve: learnedDiscardToDraw }],
});

register('hero-learned-l2', {
  // "Discard 3 cards to draw 2 cards." Draws first (an immediate, certain
  // gain), then queues 3 ordinary single-discard picks off the live hand -
  // same "cost after effect" convention already established for Reckless
  // Conjure (Root of Corruption).
  dungeon: [{
    resolve: (state, { playerId }) => {
      let nextState = drawCards(state, playerId, 2);
      for (let i = 0; i < 3; i += 1) {
        const player = nextState.players.find((p) => p.id === playerId);
        nextState = queueHandChoice(nextState, { playerId, verb: 'discard', candidateInstanceIds: player.hand, reason: 'Learned Nano: discard 3 cards.' });
      }
      return nextState;
    },
  }],
});

register('hero-learned-l3', {
  // "Draw 2 cards. Discard 1 card."
  dungeon: [{
    resolve: (state, { playerId }) => {
      const nextState = drawCards(state, playerId, 2);
      const player = nextState.players.find((p) => p.id === playerId);
      return queueHandChoice(nextState, { playerId, verb: 'discard', candidateInstanceIds: player.hand, reason: 'Enhanced Learned Nano: discard 1 card.' });
    },
  }],
});

// --- Mystic -----------------------------------------------------------------

function mysticConvert(state, { playerId, instanceId }) {
  return updatePlayer(state, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, convertedToMagicInstanceIds: [...p.turnFlags.convertedToMagicInstanceIds, instanceId] },
  }));
}

register('hero-mystic-l2', { dungeon: [{ resolve: mysticConvert }] });
register('hero-mystic-l3', { dungeon: [{ resolve: mysticConvert }] });

// --- Rugged -------------------------------------------------------------------
// "Mark 1 monster in the hall." / "After [another player/any player]
// selects a monster with your mark to fight, gain 1 XP [/take a prepare
// action]." See actions/dungeonActions.js's markMonster/resolveMonsterMark.

register('hero-rugged-l1', {
  village: [{ resolve: (state, { playerId, choices }) => markMonster(state, playerId, choices?.hallRankIndex) }],
  aftermath: [{ resolve: (state, { playerId, choices }) => markMonster(state, playerId, choices?.hallRankIndex) }],
  react: [{
    // "After ANOTHER player selects a monster with your mark to fight, gain 1 XP."
    resolve: (state, { playerId, selectingPlayerId }) => (selectingPlayerId !== playerId ? grantXp(state, playerId, 1) : state),
  }],
});
register('hero-rugged-l2', {
  aftermath: [{ resolve: (state, { playerId, choices }) => markMonster(state, playerId, choices?.hallRankIndex) }],
  react: [{
    // "After ANY player (including you) selects a monster with your mark to fight, gain 1 XP."
    resolve: (state, { playerId }) => grantXp(state, playerId, 1),
  }],
});
// "After another player selects a monster with your mark to fight, take a
// prepare action." Queues a preparePick decision (queuePreparePick) rather
// than granting it inline - actions/prepareActions.js's applyBonusPrepare
// is a deliberately turn-agnostic variant of the normal Prepare mechanic
// (core "keep N, discard rest, draw a fresh hand" only, no
// activePlayerIndex/turnFlags reset - see its own comment for why the
// normal PREPARE action's endTurn-based implementation can't be reused
// here).
register('hero-rugged-l3', {
  aftermath: [{ resolve: (state, { playerId, choices }) => markMonster(state, playerId, choices?.hallRankIndex) }],
  react: [{
    // "After ANOTHER player selects a monster with your mark to fight, take a prepare action."
    resolve: (state, { playerId, selectingPlayerId }) => (selectingPlayerId !== playerId
      ? queuePreparePick(state, { playerId, reason: 'Enhanced Rugged Jack: take a prepare action.' })
      : state),
  }],
});

// --- Stealthy ---------------------------------------------------------------

register('hero-stealthy-l1', {
  village: [{ resolve: (state, { playerId }) => grantBuys(state, playerId, 1) }],
});
register('hero-stealthy-l2', {
  village: [{ resolve: (state, { playerId }) => grantBuys(state, playerId, 1) }],
  // "Buy 1 non-hero card." Restricted to villageStacks (never heroStacks),
  // so every legal target is already non-hero - no extra filtering needed.
  spoils: [buyOneCard({
    errorMessage: 'No village cards remain to buy.',
    includeBasics: false,
  })],
});
register('hero-stealthy-l3', {
  // "Buy 1 card and add it to your hand." (both Village and Dungeon
  // timings.) "Buy" costs gold, same convention as Root of Corruption's
  // Armsman.
  village: [buyOneCard({
    errorMessage: 'No village cards remain to buy.',
    includeBasics: false,
    buyOptions: { destination: 'hand' },
  })],
  dungeon: [buyOneCard({
    errorMessage: 'No village cards remain to buy.',
    includeBasics: false,
    buyOptions: { destination: 'hand' },
  })],
});

// --- Strong -------------------------------------------------------------------
// "specialEffects: You cannot level up a Regular into this card." -
// village/leveling.js's NO_REGULAR_LEVEL_UP_GROUPS.

function strongSpoilsLevelUp(xpCost) {
  return (state, { playerId, instanceId }) => {
    const player = state.players.find((p) => p.id === playerId);
    if (player.xp < xpCost) return state;
    const paid = grantXp(state, playerId, -xpCost);
    return performLevelUp(paid, playerId, instanceId, { payXp: false, destination: 'discard' });
  };
}

register('hero-strong-l1', { spoils: [{ resolve: strongSpoilsLevelUp(4) }] });
register('hero-strong-l2', { spoils: [{ resolve: strongSpoilsLevelUp(6) }] });

// --- Strong-willed --------------------------------------------------------
// "Use the Dungeon ability on one item card even if you have already used
// it this turn." (L2) - the same "meta-ability, bypass the usage cap"
// shape as Root of Corruption's Debased Wizard (effectRegistry/villages.js's
// debasedWizardReuse/debasedWizardReusableCandidates), narrowed to Item-
// trait cards' own Dungeon lines specifically rather than any card's
// ability of the current timing. The reused ability was already
// legitimately used once this turn (that's the precondition) - no
// bookkeeping needed to "unblock" it afterward, it's already marked used
// and stays that way. Silently no-ops if nothing eligible exists (same "do
// what you can" convention as Debased Wizard's own reuse line).
function strongWilledItemReuseCandidates(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const candidates = [];
  for (const instanceId of player.hand) {
    const def = getInstanceDefinition(state, instanceId);
    if (!def.traits.includes('Item')) continue;
    (def.abilityText.dungeon || []).forEach((entry, abilityIndex) => {
      const key = `${instanceId}:dungeon:${abilityIndex}`;
      if (player.turnFlags.usedAbilityInstanceIds.includes(key) && getHandler(def.id, 'dungeon', abilityIndex)) {
        candidates.push({ instanceId, abilityIndex });
      }
    });
  }
  return candidates;
}
register('hero-strongwilled-l2', {
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const candidates = strongWilledItemReuseCandidates(state, playerId);
      if (!candidates.length) return state;
      const chosen = choices?.targetInstanceId
        ? candidates.find((c) => c.instanceId === choices.targetInstanceId
          && (choices.targetAbilityIndex === undefined || c.abilityIndex === choices.targetAbilityIndex))
        : candidates[0];
      if (!chosen) throw new Error('That item\'s Dungeon ability is not eligible to be reused - it must already be used this turn.');
      const def = getInstanceDefinition(state, chosen.instanceId);
      const handler = getHandler(def.id, 'dungeon', chosen.abilityIndex);
      return handler(state, { playerId, instanceId: chosen.instanceId, choices: choices?.targetChoices || {} });
    },
  }],
});

register('hero-strongwilled-l3', {
  // "Each other player discards 1 item or shows a hand with none. Add
  // Physical Attack equal to the cost of 1 discarded card." Simplified: for
  // each other player, discards their first item present (if any); grants
  // Physical Attack equal to the LAST discarded item's cost (an arbitrary
  // but reasonable pick among the discarded costs when several players
  // have one, since the printed text only names "1 discarded card" without
  // saying which).
  dungeon: [{
    resolve: (state, { playerId }) => {
      let nextState = state;
      let lastCost = 0;
      for (const p of state.players) {
        if (p.id === playerId) continue;
        const current = nextState.players.find((pp) => pp.id === p.id);
        const itemId = current.hand.find((id) => getInstanceDefinition(nextState, id).traits.includes('Item'));
        if (!itemId) continue;
        lastCost = getInstanceDefinition(nextState, itemId).cost || 0;
        nextState = discardFromHand(nextState, p.id, itemId);
      }
      return lastCost ? grantPhysicalAttack(nextState, playerId, lastCost) : nextState;
    },
  }],
});

// --- Swift --------------------------------------------------------------------
// "Mark 1 non-guardian monster in the hall." (Aftermath) / "After ANOTHER
// player selects a monster with your mark to fight, add Health +N to that
// monster." (React) Same mechanism as Rugged above, punishing instead of
// rewarding.

function swiftReact(bonus) {
  return (state, { playerId, monsterInstanceId, selectingPlayerId }) => {
    if (selectingPlayerId === playerId) return state;
    const def = getInstanceDefinition(state, monsterInstanceId);
    return applyInstanceOverrideHealth(state, monsterInstanceId, (def.health || 0) + bonus);
  };
}
function applyInstanceOverrideHealth(state, instanceId, health) {
  return { ...state, instances: { ...state.instances, [instanceId]: { ...state.instances[instanceId], health } } };
}

register('hero-swift-l1', {
  aftermath: [{ resolve: (state, { playerId, choices }) => markMonster(state, playerId, choices?.hallRankIndex, { nonGuardianOnly: true }) }],
  react: [{ resolve: swiftReact(1) }],
});
register('hero-swift-l2', {
  aftermath: [{ resolve: (state, { playerId, choices }) => markMonster(state, playerId, choices?.hallRankIndex, { nonGuardianOnly: true }) }],
  react: [{ resolve: swiftReact(2) }],
});
register('hero-swift-l3', {
  aftermath: [{ resolve: (state, { playerId, choices }) => markMonster(state, playerId, choices?.hallRankIndex, { nonGuardianOnly: true }) }],
  react: [{ resolve: swiftReact(3) }],
});

// --- Tough --------------------------------------------------------------------

function toughDrawIfEquipped(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  return getEquippedWeaponIds(player, instanceId).length ? drawCards(state, playerId, 1) : state;
}

register('hero-tough-l1', {
  dungeon: [{ resolve: toughDrawIfEquipped }],
  // "Buy 1 weapon." Free, like every other ToR Spoils-buy (Bounty Hunter's
  // own precedent).
  spoils: [buyOneCard({
    matches: (def) => def.traits.includes('Weapon'),
    errorMessage: 'No weapon remains to buy.',
    includeBasics: false,
  })],
});
register('hero-tough-l2', {
  dungeon: [{ resolve: toughDrawIfEquipped }],
  spoils: [{
    // "Place the top card of the Tough stack into your discard pile." A
    // free copy of itself, independent of what's currently in hand -
    // distinct from performLevelUp (this isn't leveling anything up).
    resolve: (state, { playerId }) => {
      const stack = state.village.heroStacks.tough || [];
      if (!stack.length) return state;
      const [topInstanceId, ...rest] = stack;
      let nextState = { ...state, village: { ...state.village, heroStacks: { ...state.village.heroStacks, tough: rest } } };
      nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, discard: [...p.discard, topInstanceId] }));
      return nextState;
    },
  }],
});
register('hero-tough-l3', {
  dungeon: [{ resolve: toughDrawIfEquipped }],
  spoils: [{
    // "Place the top card of any hero stack into your discard pile."
    resolve: (state, { playerId, choices }) => {
      const groupKey = choices?.heroGroupKey ?? Object.keys(state.village.heroStacks).find((k) => state.village.heroStacks[k].length);
      const stack = groupKey ? state.village.heroStacks[groupKey] : null;
      if (!stack || !stack.length) return state;
      const [topInstanceId, ...rest] = stack;
      let nextState = { ...state, village: { ...state.village, heroStacks: { ...state.village.heroStacks, [groupKey]: rest } } };
      nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, discard: [...p.discard, topInstanceId] }));
      return nextState;
    },
  }],
});

// ==========================================================================
// Starter set
// ==========================================================================

// --- Blackhand ------------------------------------------------------------
// "specialEffects: If you purchase a hero or villager this turn, you may
// place it on top of your deck instead of in your discard pile." -
// actions/villageActions.js's performBuy.

register('hero-blackhand-l2', {
  dungeon: [{
    // "Add Physical Attack +2 if two or more other heroes are present."
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const otherHeroCount = player.hand.filter((id) => id !== instanceId && isHeroLike(getInstanceDefinition(state, id))).length;
      return otherHeroCount >= 2 ? grantPhysicalAttack(state, playerId, 2) : state;
    },
  }],
});
register('hero-blackhand-l3', {
  dungeon: [{
    // "Add Physical Attack +4 if one or more other heroes are present."
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const otherHeroCount = player.hand.filter((id) => id !== instanceId && isHeroLike(getInstanceDefinition(state, id))).length;
      return otherHeroCount >= 1 ? grantPhysicalAttack(state, playerId, 4) : state;
    },
  }],
});

// --- Cathedral --------------------------------------------------------------

function cathedralCureForCard(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
  return diseaseId ? drawCards(destroyFromHand(state, playerId, diseaseId), playerId, 1) : state;
}
register('hero-cathedral-l1', {
  village: [{ resolve: cathedralCureForCard }],
  dungeon: [{ resolve: cathedralCureForCard }],
});

function cathedralDestroyForCard(state, { playerId, choices }) {
  const player = state.players.find((p) => p.id === playerId);
  const targetId = choices?.instanceId ?? player.hand[0];
  return targetId ? drawCards(destroyFromHand(state, playerId, targetId), playerId, 1) : state;
}
function cathedralDestroyForCardCandidates(state, { playerId }) {
  return state.players.find((p) => p.id === playerId).hand;
}
register('hero-cathedral-l2', {
  village: [{ resolve: cathedralDestroyForCard, getCandidates: cathedralDestroyForCardCandidates }],
  dungeon: [{ resolve: cathedralDestroyForCard, getCandidates: cathedralDestroyForCardCandidates }],
});

register('hero-cathedral-l3', {
  dungeon: [{
    // "Destroy 1 card to add Magic Attack +3 to this card." (the bonus
    // lands in the shared Magic Attack pool regardless of "to this card" -
    // same convention as every other pooled-attack grant in this file.)
    resolve: (state, { playerId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetId = choices?.instanceId ?? player.hand[0];
      return targetId ? grantMagicAttack(destroyFromHand(state, playerId, targetId), playerId, 3) : state;
    },
    getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand,
  }],
});

// --- Garrison -----------------------------------------------------------

function garrisonAttackIfEquipped(amount) {
  return (state, { playerId, instanceId }) => {
    const player = state.players.find((p) => p.id === playerId);
    return getEquippedWeaponIds(player, instanceId).length ? grantPhysicalAttack(state, playerId, amount) : state;
  };
}
register('hero-garrison-l2', { dungeon: [{ resolve: garrisonAttackIfEquipped(1) }] });
register('hero-garrison-l3', { dungeon: [{ resolve: garrisonAttackIfEquipped(3) }] });

// --- Tower --------------------------------------------------------------------

register('hero-tower-l2', { dungeon: [{ resolve: (state, { playerId }) => drawCards(state, playerId, 1) }] });
register('hero-tower-l3', { dungeon: [{ resolve: (state, { playerId }) => drawCards(state, playerId, 2) }] });

// --- Wildwood -----------------------------------------------------------------

function wildwoodMonsterCount(state, { playerId }) {
  const count = state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).type === 'monster').length;
  return count ? grantPhysicalAttack(state, playerId, count) : state;
}
register('hero-wildwood-l2', { dungeon: [{ resolve: wildwoodMonsterCount }] });
register('hero-wildwood-l3', {
  dungeon: [{ resolve: wildwoodMonsterCount }],
  spoils: [{
    // "Place 1 non-Guardian monster from the hall on the bottom of the
    // dungeon deck. Refill the hall." Same hallRankIndex-choice fallback
    // convention as Veilminder/Tuath's own hall-targeting abilities.
    resolve: (state, { playerId, choices }) => {
      const candidateRanks = state.dungeonHall
        .map((id, i) => i)
        .filter((i) => state.dungeonHall[i] && !getInstanceDefinition(state, state.dungeonHall[i]).traits.includes('Guardian'));
      const targetRank = choices?.hallRankIndex ?? candidateRanks[0];
      if (targetRank === undefined || !candidateRanks.includes(targetRank)) return state;
      const monsterInstanceId = state.dungeonHall[targetRank];
      const dungeonHall = [...state.dungeonHall];
      dungeonHall[targetRank] = null;
      return compactAndRefill({ ...state, dungeonHall, dungeonDeck: [...state.dungeonDeck, monsterInstanceId] }, { playerId });
    },
  }],
});

// ==========================================================================
// Into the Abyss
// ==========================================================================
// Every group prints 4 levels (level4, not the usual level3 cap) - see
// buildCardData.mjs's COPY_COUNTS_BY_LEVEL_COUNT and village/leveling.js's
// ascendHeroToLevel4 for the 3->4 mechanism itself (a Spoils-window action,
// not a normal Village level-up - Matt confirmed this directly). Nothing
// here needs to special-case that transition - each level's own Village/
// Dungeon/Spoils ability lines are implemented exactly like every other
// set's, independent of how the card reached that level.

// --- Blind ----------------------------------------------------------------
// L3/L4's "Ignore Darkness penalties." and "Add Magic Attack +3/+4 if no
// Light items are present" - combat/resolveCombat.js (Darkness-ignore, hand-
// presence check below) and combat/attackMath.js contributesTraits (Magic
// Attack bonus). L4's additional "...and ignore Battle effects if no Light
// items are present" clause is NOT implemented - see docs/expansion-audit.md
// (a compound condition gating a whole-player Battle-effect immunity, a
// different shape from isShieldedByPike's per-hero-targeting scope).

function blindDestroyLightItemForMagic(amount) {
  return (state, { playerId, choices }) => {
    const player = state.players.find((p) => p.id === playerId);
    const candidates = player.hand.filter((id) => {
      const t = getInstanceDefinition(state, id).traits || [];
      return t.includes('Item') && t.includes('Light');
    });
    const targetId = choices?.instanceId ?? candidates[0];
    if (!targetId) return state;
    return grantMagicAttack(destroyFromHand(state, playerId, targetId), playerId, amount);
  };
}
register('hero-blind-l1', { dungeon: [{ resolve: blindDestroyLightItemForMagic(3) }] });
register('hero-blind-l2', { dungeon: [{ resolve: blindDestroyLightItemForMagic(4) }] });

// --- Chalice ----------------------------------------------------------------

function chaliceDestroyDiseaseToDraw(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
  return diseaseId ? drawCards(destroyFromHand(state, playerId, diseaseId), playerId, 1) : state;
}
register('hero-chalice-l1', { dungeon: [{ resolve: chaliceDestroyDiseaseToDraw }] });

register('hero-chalice-l2', {
  dungeon: [
    {
      // "Add Physical Attack +1 for each Light item present."
      resolve: (state, { playerId }) => {
        const count = state.players.find((p) => p.id === playerId).hand.filter((id) => {
          const t = getInstanceDefinition(state, id).traits || [];
          return t.includes('Item') && t.includes('Light');
        }).length;
        return count ? grantPhysicalAttack(state, playerId, count) : state;
      },
    },
    { resolve: (state, { playerId }) => drawCards(state, playerId, 1) },
    { resolve: chaliceDestroyDiseaseToDraw },
  ],
});

const chaliceBuyOneCard = buyOneCard({ onEmpty: 'skip', includeBasics: false });
register('hero-chalice-l3', {
  dungeon: [{ resolve: (state, { playerId }) => drawCards(state, playerId, 1) }],
  spoils: [chaliceBuyOneCard],
});

function chaliceDrawWithLightBonus(count) {
  return (state, { playerId }) => {
    const before = new Set(state.players.find((p) => p.id === playerId).hand);
    let nextState = drawCards(state, playerId, count);
    const drawnIds = nextState.players.find((p) => p.id === playerId).hand.filter((id) => !before.has(id));
    const hasLight = drawnIds.some((id) => (getInstanceDefinition(nextState, id).light || 0) > 0);
    // "you may draw 2 more cards" - defaults to accepting (no real downside
    // to drawing more cards), same "you may" convention used throughout.
    if (hasLight) nextState = drawCards(nextState, playerId, 2);
    return nextState;
  };
}
// The FIRST buy is an ordinary player-chosen buyOneCard (sharing
// chaliceBuyOneCard's own candidate list). The bonus second buy that a Light
// card triggers still auto-picks: it only becomes available part-way through
// resolving this same handler, so offering it needs a second, sequential
// prompt rather than the single up-front pick every other buy uses - the same
// shape Trader/Aeon's own trade-up already handles specially in the dock.
// Noted rather than fixed here; the reported cards (Clan Champion, Isri
// Bladespinner) are the plain single-buy shape.
function chaliceBuyWithLightBonus(state, { playerId, choices }) {
  const sources = chaliceBuyOneCard.getBuyableSources(state);
  const chosen = (choices?.source && sources.find((s) => s.type === choices.source.type && s.key === choices.source.key))
    || sources.find((s) => s.type === 'village' && s.key === choices?.villageDefId)
    || sources[0];
  if (!chosen) return state;
  const boughtDef = getInstanceDefinition(state, state.village.villageStacks[chosen.key][0]);
  let nextState = performBuy(state, playerId, chosen, { requireGoldCheck: false });
  if ((boughtDef.light || 0) > 0) {
    const keys2 = Object.keys(nextState.village.villageStacks).filter((k) => nextState.village.villageStacks[k].length);
    const key2 = choices?.secondVillageDefId ?? keys2[0];
    if (key2) nextState = performBuy(nextState, playerId, { type: 'village', key: key2 }, { requireGoldCheck: false });
  }
  return nextState;
}
register('hero-chalice-l4', {
  dungeon: [{ resolve: chaliceDrawWithLightBonus(2) }],
  spoils: [{ getBuyableSources: chaliceBuyOneCard.getBuyableSources, resolve: chaliceBuyWithLightBonus }],
});

// --- Grim -------------------------------------------------------------------
// "Spoils: Set the defeated monster [and this card / and any monsters
// present] aside. Return [it/them] to your deck at the end of the game
// [(L2: discard 2 XP to do so)]." effectHelpers.js's setAsideForGame - the
// just-defeated monster is already sitting in discard by the time Spoils
// fires (see useSpoilsAbility's own comment), identified via
// state.meta.lastCombatResult.monsterInstanceId (only ever the CURRENT
// turn's single fight - "your party is working together to slay ONE
// monster" per turn, so no staleness risk within the Spoils window).
register('hero-grim-l1', {
  // "Set the defeated monster and this card aside."
  spoils: [{
    resolve: (state, { playerId, instanceId }) => {
      const monsterInstanceId = state.meta.lastCombatResult?.monsterInstanceId;
      const nextState = monsterInstanceId ? setAsideForGame(state, playerId, monsterInstanceId) : state;
      return setAsideForGame(nextState, playerId, instanceId);
    },
  }],
});
register('hero-grim-l2', {
  // "Discard 2 XP to set the defeated monster aside." Silently no-ops if
  // the player can't afford it (same "do what you can" convention as most
  // other XP-gated abilities in this file) - Spoils abilities are already
  // entirely optional to begin with.
  spoils: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      if (player.xp < 2) return state;
      const monsterInstanceId = state.meta.lastCombatResult?.monsterInstanceId;
      if (!monsterInstanceId) return state;
      return setAsideForGame(grantXp(state, playerId, -2), playerId, monsterInstanceId);
    },
  }],
});
register('hero-grim-l3', {
  // "Set the defeated monster aside." (unconditional, no self, no XP cost)
  spoils: [{
    resolve: (state, { playerId }) => {
      const monsterInstanceId = state.meta.lastCombatResult?.monsterInstanceId;
      return monsterInstanceId ? setAsideForGame(state, playerId, monsterInstanceId) : state;
    },
  }],
});
register('hero-grim-l4', {
  // "Set the defeated monster and any monsters present aside." Monster-type
  // cards essentially never sit in hand under normal play (see
  // effectHelpers.js's isHeroLike comment on the same recurring caveat) -
  // correct as written even though the "any monsters present" half will
  // rarely trigger.
  spoils: [{
    resolve: (state, { playerId }) => {
      const monsterInstanceId = state.meta.lastCombatResult?.monsterInstanceId;
      let nextState = monsterInstanceId ? setAsideForGame(state, playerId, monsterInstanceId) : state;
      const player = nextState.players.find((p) => p.id === playerId);
      const otherMonsterIds = player.hand.filter((id) => getInstanceDefinition(nextState, id).type === 'monster');
      for (const id of otherMonsterIds) nextState = setAsideForGame(nextState, playerId, id);
      return nextState;
    },
  }],
});

// --- Lorigg -------------------------------------------------------------------
// L1 has no triggered ability text (Physical Attack +1 only) - no register() needed.

function lorigg1CardChoice(state, { playerId }) {
  let nextState = state;
  for (const p of state.players) {
    if (p.id === playerId) continue;
    const current = nextState.players.find((pp) => pp.id === p.id);
    if (current.hand.length) nextState = discardFromHand(nextState, p.id, current.hand[0]);
  }
  return nextState;
}
register('hero-lorigg-l2', { dungeon: [{ resolve: lorigg1CardChoice }] });
register('hero-lorigg-l3', { dungeon: [{ resolve: lorigg1CardChoice }] });

function loriggDiscardHighestLevelHero(state, { playerId }) {
  let nextState = state;
  for (const p of state.players) {
    if (p.id === playerId) continue;
    const current = nextState.players.find((pp) => pp.id === p.id);
    const heroes = current.hand
      .map((id) => ({ id, def: getInstanceDefinition(nextState, id) }))
      .filter(({ def }) => isHeroLike(def));
    if (!heroes.length) continue;
    const highest = heroes.reduce((best, h) => ((h.def.level || 1) > (best.def.level || 1) ? h : best));
    nextState = discardFromHand(nextState, p.id, highest.id);
  }
  return nextState;
}
register('hero-lorigg-l4', {
  village: [{ resolve: loriggDiscardHighestLevelHero }],
  dungeon: [{ resolve: loriggDiscardHighestLevelHero }],
});

// --- Outlands -----------------------------------------------------------------

function outlandsDestroyFoodForAttack(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  const foodId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Food'));
  return foodId ? grantPhysicalAttack(destroyFromHand(state, playerId, foodId), playerId, 3) : state;
}
register('hero-outlands-l1', { dungeon: [{ resolve: outlandsDestroyFoodForAttack }] });

function outlandsMonsterCountBonus(amount) {
  return (state, { playerId }) => {
    const count = state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).type === 'monster').length;
    return count ? grantPhysicalAttack(state, playerId, count * amount) : state;
  };
}
register('hero-outlands-l2', {
  dungeon: [{ resolve: outlandsMonsterCountBonus(1) }, { resolve: outlandsDestroyFoodForAttack }],
});
register('hero-outlands-l3', { dungeon: [{ resolve: outlandsMonsterCountBonus(2) }] });

register('hero-outlands-l4', {
  // "Add Physical Attack equal to the total XP of all monsters present."
  // Monster-type cards essentially never sit in hand under normal play (see
  // effectHelpers.js's isHeroLike comment) - correct as written even though
  // it will rarely trigger.
  dungeon: [{
    resolve: (state, { playerId }) => {
      const total = state.players.find((p) => p.id === playerId).hand
        .map((id) => getInstanceDefinition(state, id))
        .filter((def) => def.type === 'monster')
        .reduce((sum, def) => sum + (def.xp || 0), 0);
      return total ? grantPhysicalAttack(state, playerId, total) : state;
    },
  }],
});

// --- Regian -------------------------------------------------------------------

// Matt reported this directly: "When I use the Regian ability to destroy a
// disease. I should get to choose which disease to destroy." Same
// first-found-via-.find() gap already fixed on Honormain's own disease line
// (see honormainCureForMagicCandidates above) - getCandidates fixes it the
// same way here.
function regianDiseaseCandidates(state, { playerId }) {
  return state.players.find((p) => p.id === playerId).hand
    .filter((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
}
function regianDestroyDiseaseToDraw(count) {
  return (state, { playerId, choices }) => {
    const targetId = choices?.instanceId ?? regianDiseaseCandidates(state, { playerId })[0];
    return targetId ? drawCards(destroyFromHand(state, playerId, targetId), playerId, count) : state;
  };
}
register('hero-regian-l1', { dungeon: [{ resolve: regianDestroyDiseaseToDraw(1), getCandidates: regianDiseaseCandidates }] });
register('hero-regian-l2', {
  dungeon: [
    { resolve: (state, { playerId }) => drawCards(state, playerId, 1) },
    { resolve: regianDestroyDiseaseToDraw(1), getCandidates: regianDiseaseCandidates },
  ],
});
register('hero-regian-l3', {
  dungeon: [
    { resolve: (state, { playerId }) => drawCards(state, playerId, 2) },
    { resolve: regianDestroyDiseaseToDraw(2), getCandidates: regianDiseaseCandidates },
  ],
});
register('hero-regian-l4', {
  dungeon: [
    { resolve: (state, { playerId }) => drawCards(state, playerId, 3) },
    { resolve: regianDestroyDiseaseToDraw(3), getCandidates: regianDiseaseCandidates },
  ],
});

// --- Thornwood ----------------------------------------------------------------
// "Add Physical Attack +N for each rank of the monster you are fighting" (all
// 4 levels) / "Add Light +1 against the highest rank" (L2) -
// combat/attackMath.js contributesTraits (hallRankIndex-based, see below).

register('hero-thornwood-l3', {
  // "Spoils: Gain 1 XP if you defeated the monster in the highest rank."
  spoils: [{
    resolve: (state, { playerId }) => {
      const result = state.meta.lastCombatResult;
      if (!result || !result.won) return state;
      const highestRankIndex = state.dungeonHall.length - 1;
      return result.hallRankIndex === highestRankIndex ? grantXp(state, playerId, 1) : state;
    },
  }],
});
register('hero-thornwood-l4', {
  // "Spoils: Discard 5 XP to take another turn." Same state.meta.extraTurnGranted
  // mechanism as village-fearmaker's own "take another turn" (ToR).
  spoils: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      if (player.xp < 5) return state;
      const nextState = grantXp(state, playerId, -5);
      return { ...nextState, meta: { ...nextState.meta, extraTurnGranted: true } };
    },
  }],
});

// --- Verdan -------------------------------------------------------------------

register('hero-verdan-l1', {
  // "Each other player with 6 or more cards discards 1 random card." - the
  // first card needing "pick 1 random card from a player's HAND"; see
  // effectHelpers.js's pickRandomInstanceId.
  dungeon: [{
    resolve: (state, { playerId }) => {
      let nextState = state;
      for (const p of state.players) {
        if (p.id === playerId) continue;
        const current = nextState.players.find((pp) => pp.id === p.id);
        if (current.hand.length < 6) continue;
        const { state: afterPick, instanceId } = pickRandomInstanceId(nextState, current.hand);
        if (instanceId) nextState = discardFromHand(afterPick, p.id, instanceId);
      }
      return nextState;
    },
  }],
});

function verdanBanishRank(rankIndex) {
  return (state) => {
    const targetRank = rankIndex ?? state.dungeonHall.findIndex(Boolean);
    if (targetRank === -1 || targetRank === undefined || !state.dungeonHall[targetRank]) return state;
    const monsterInstanceId = state.dungeonHall[targetRank];
    const dungeonHall = [...state.dungeonHall];
    dungeonHall[targetRank] = null;
    return compactAndRefill({ ...state, dungeonHall, dungeonDeck: [...state.dungeonDeck, monsterInstanceId] }, {});
  };
}

// "Each other player reveals 1 spell or discards 1 card." Since this
// engine's game state has no hidden information to begin with (hands are
// already fully visible - see village-warder's own comment on the same
// point), "reveal" has no separate observable effect here: implemented as
// "if that player has no spell present, they discard 1 card" (the only
// branch with any real state change), rather than modeling a no-op reveal
// choice.
function verdanRevealSpellOrDiscard(state, playerId) {
  let nextState = state;
  for (const p of state.players) {
    if (p.id === playerId) continue;
    const current = nextState.players.find((pp) => pp.id === p.id);
    const hasSpell = current.hand.some((id) => getInstanceDefinition(nextState, id).traits.includes('Spell'));
    if (hasSpell || !current.hand.length) continue;
    nextState = discardFromHand(nextState, p.id, current.hand[0]);
  }
  return nextState;
}

register('hero-verdan-l2', {
  dungeon: [
    {
      // "Discard 2 XP to place the rank 1 monster on the bottom of the
      // dungeon deck. Refill the hall."
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        if (player.xp < 2 || !state.dungeonHall[0]) return state;
        return verdanBanishRank(0)(grantXp(state, playerId, -2));
      },
    },
    { resolve: (state, { playerId }) => verdanRevealSpellOrDiscard(state, playerId) },
  ],
});
register('hero-verdan-l3', {
  dungeon: [
    {
      // "Discard 1 XP to place a monster from the hall on the bottom of the
      // dungeon deck. Refill the hall."
      resolve: (state, { playerId, choices }) => {
        const player = state.players.find((p) => p.id === playerId);
        if (player.xp < 1) return state;
        const targetRank = choices?.hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
        if (targetRank === -1 || !state.dungeonHall[targetRank]) return state;
        return verdanBanishRank(targetRank)(grantXp(state, playerId, -1));
      },
    },
    {
      // "Each other player discards 1 XP."
      resolve: (state, { playerId }) => {
        let nextState = state;
        for (const p of state.players) {
          if (p.id === playerId) continue;
          nextState = grantXp(nextState, p.id, -1);
        }
        return nextState;
      },
    },
  ],
});
register('hero-verdan-l4', {
  dungeon: [
    {
      // "Place a monster from the hall on the bottom of the dungeon deck.
      // Refill the hall." Free (no XP cost, unlike L2/L3).
      resolve: (state, { choices }) => {
        const targetRank = choices?.hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
        if (targetRank === -1 || !state.dungeonHall[targetRank]) return state;
        return verdanBanishRank(targetRank)(state);
      },
    },
    {
      // "Each other player discards 1 XP and 1 random card."
      resolve: (state, { playerId }) => {
        let nextState = state;
        for (const p of state.players) {
          if (p.id === playerId) continue;
          nextState = grantXp(nextState, p.id, -1);
          const current = nextState.players.find((pp) => pp.id === p.id);
          const { state: afterPick, instanceId } = pickRandomInstanceId(nextState, current.hand);
          nextState = instanceId ? discardFromHand(afterPick, p.id, instanceId) : afterPick;
        }
        return nextState;
      },
    },
  ],
});

// --- Veris --------------------------------------------------------------------
// "Add [Physical Attack +N and] Light +N to each equipped weapon" (all 4
// levels, alwaysActive) - combat/attackMath.js's VERIS_WEAPON_BONUS table,
// applied per equipped weapon in attackMath.js's contributesTraits
// (physicalAttack half) and lightDarkness.js's computeTotalLight (light
// half). No register() entry needed here - alwaysActive traits are read
// generically off hand contents, not dispatched through this registry.
// L3/L4's Dungeon lines (Physical/Magic Attack equal to total Light
// present) ARE implemented below.

register('hero-veris-l3', {
  dungeon: [{
    resolve: (state, { playerId }) => {
      const total = computeTotalLight(state, playerId);
      return total ? grantPhysicalAttack(state, playerId, total) : state;
    },
  }],
});
register('hero-veris-l4', {
  dungeon: [{
    resolve: (state, { playerId }) => {
      const total = computeTotalLight(state, playerId);
      return total ? grantMagicAttack(state, playerId, total) : state;
    },
  }],
});

// --- Xenomorph ----------------------------------------------------------------
// "This card becomes a duplicate of an equal or lower level non-Xenomorph
// hero present, or in your discard pile, or [on top of / in] a hero stack."
// (all 4 levels, both Village and Dungeon timings) - becomeDuplicateOf
// (effectHelpers.js) does a full identity replace (instance.definitionId),
// not a stat override. Candidates: hand + discard always; L2/L3 also allow
// the TOP of any village hero stack; L4 allows anywhere within a stack
// ("even if not on top") - "present" candidates are read by DEFINITION,
// not consumed/removed (a hero-stack card stays exactly where it is; this
// card just copies its identity). Level-gated to "equal or lower," and
// "non-Xenomorph" excludes copying another Xenomorph card (this trait).
function xenomorphDuplicateCandidates(state, playerId, instanceId, stackDepth) {
  const player = state.players.find((p) => p.id === playerId);
  const selfDef = getInstanceDefinition(state, instanceId);
  const isEligible = (def) => isHeroLike(def) && !def.traits.includes('Xenomorph') && (def.level ?? 0) <= selfDef.level;
  const fromZone = (ids) => ids.filter((id) => id !== instanceId && isEligible(getInstanceDefinition(state, id)));
  const candidates = [...fromZone(player.hand), ...fromZone(player.discard)];
  if (stackDepth) {
    for (const groupKey of Object.keys(state.village.heroStacks)) {
      const stack = state.village.heroStacks[groupKey];
      const relevant = stackDepth === 'top' ? stack.slice(0, 1) : stack;
      candidates.push(...fromZone(relevant));
    }
  }
  return candidates;
}
function xenomorphBecomeDuplicate(stackDepth) {
  const getCandidates = (state, { playerId, instanceId }) => xenomorphDuplicateCandidates(state, playerId, instanceId, stackDepth);
  return {
    resolve: (state, { playerId, instanceId, choices }) => {
      const candidates = getCandidates(state, { playerId, instanceId });
      if (choices?.targetInstanceId && !candidates.includes(choices.targetInstanceId)) {
        throw new Error('That card is not an eligible target - it must be an equal-or-lower-level non-Xenomorph hero present, in your discard pile, or in a hero stack.');
      }
      if (!candidates.length) return state;
      const targetInstanceId = choices?.targetInstanceId ?? candidates[0];
      return becomeDuplicateOf(state, instanceId, getInstanceDefinition(state, targetInstanceId).id);
    },
    getCandidates,
  };
}
register('hero-xenomorph-l1', {
  village: [xenomorphBecomeDuplicate(null)],
  dungeon: [xenomorphBecomeDuplicate(null)],
});
register('hero-xenomorph-l2', {
  village: [xenomorphBecomeDuplicate('top')],
  dungeon: [xenomorphBecomeDuplicate('top')],
});

function xenomorphDestroyTopOfHeroStack(state, { choices }) {
  const groupKey = choices?.heroGroupKey ?? Object.keys(state.village.heroStacks).find((k) => state.village.heroStacks[k].length);
  const stack = groupKey ? state.village.heroStacks[groupKey] : null;
  if (!stack || !stack.length) return state;
  const [, ...rest] = stack;
  return { ...state, village: { ...state.village, heroStacks: { ...state.village.heroStacks, [groupKey]: rest } } };
}
// Xenomorph Shifter (L3) prints 2 lines under both Village and Dungeon:
// "Destroy the top card of a hero stack." and "This card becomes a
// duplicate..." - both registered below (destroy first, matching the
// printed line order).
register('hero-xenomorph-l3', {
  village: [{ resolve: xenomorphDestroyTopOfHeroStack }, xenomorphBecomeDuplicate('top')],
  dungeon: [{ resolve: xenomorphDestroyTopOfHeroStack }, xenomorphBecomeDuplicate('top')],
});
register('hero-xenomorph-l4', {
  village: [xenomorphBecomeDuplicate('any')],
  dungeon: [xenomorphBecomeDuplicate('any')],
});

// ==========================================================================
// Worlds Collide
// ==========================================================================
// No rulebook available (same caveat as Numenera/Start/ItA) - extra caution
// on anything ambiguous. Every group prints a real, distinct 4th level -
// Matt confirmed the same ascension mechanic as Into the Abyss applies
// (see docs/expansion-audit.md's Scope section), so no register() work is
// needed for the 3->4 step itself, only each level's own printed ability.

// --- Bluefire -------------------------------------------------------------

register('hero-bluefire-l1', { dungeon: [{ resolve: (state, { playerId }) => grantXp(state, playerId, 1) }] });
register('hero-bluefire-l2', { dungeon: [{ resolve: (state, { playerId }) => grantXp(state, playerId, 2) }] });

function bluefireLevelUpCandidates(state, { playerId, instanceId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => {
    if (id === instanceId) return false;
    const def = getInstanceDefinition(state, id);
    return isHeroLike(def) && def.level < 3 && !player.turnFlags.leveledHeroInstanceIds.includes(id) && player.xp >= (def.xp || 0);
  });
}
// "Level up 1 hero (pay all costs). Place the new hero in your hand." - same
// shape as Whetmage's own L1/L2 (pay XP normally, land in hand), reused here
// rather than re-derived - see hero-whetmage-l1's own comment above for why
// "pay all costs" means XP is NOT waived.
register('hero-bluefire-l3', {
  dungeon: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const candidates = bluefireLevelUpCandidates(state, { playerId, instanceId });
      const targetId = choices?.heroInstanceId ?? candidates[0];
      if (!targetId) throw new Error('No hero available to level up.');
      return performLevelUp(state, playerId, targetId, { destination: 'hand' });
    },
    getCandidates: bluefireLevelUpCandidates,
  }],
});
register('hero-bluefire-l4', {
  // "...Instead of destroying the hero you level with this ability, you may
  // return it to your hand." village/leveling.js's new keepOldHeroInHand
  // option - opted into via choices.keepOldHero, defaulting to false (the
  // normal destroy) since "you may" is an opt-in, not automatic.
  dungeon: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      const candidates = bluefireLevelUpCandidates(state, { playerId, instanceId });
      const targetId = choices?.heroInstanceId ?? candidates[0];
      if (!targetId) throw new Error('No hero available to level up.');
      return performLevelUp(state, playerId, targetId, { destination: 'hand', keepOldHeroInHand: Boolean(choices?.keepOldHero) });
    },
    getCandidates: bluefireLevelUpCandidates,
  }],
});

// --- Clan -------------------------------------------------------------------

// "Add Physical Attack +N to each OTHER equipped hero." Granted once, at
// Dungeon-ability-use time, scaled by how many OTHER heroes (not this card
// itself) currently have at least 1 weapon equipped - same "count present,
// multiply by N" convention as Glamercast's own "give each other hero X"
// alwaysActive aura, just activated instead of passive.
function clanOtherEquippedBonus(amount) {
  return (state, { playerId, instanceId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const otherEquippedCount = Object.keys(player.equippedWeapons).filter((heroId) => heroId !== instanceId).length;
    return otherEquippedCount ? grantPhysicalAttack(state, playerId, amount * otherEquippedCount) : state;
  };
}
register('hero-clan-l1', { dungeon: [{ resolve: clanOtherEquippedBonus(1) }] });
register('hero-clan-l2', { dungeon: [{ resolve: clanOtherEquippedBonus(2) }] });
register('hero-clan-l3', {
  dungeon: [{ resolve: clanOtherEquippedBonus(3) }],
  // "Spoils: Buy 1 weapon." Matt: "The clan champion spoils ability didn't
  // let me choose what weapon to buy." It used to take the cheapest weapon
  // outright (findCheapestBuyableSource) - buyOneCard offers the real list.
  spoils: [buyOneCard({
    matches: (def) => def.traits.includes('Weapon'),
    onEmpty: 'skip',
  })],
});
register('hero-clan-l4', {
  dungeon: [
    { resolve: clanOtherEquippedBonus(4) },
    (() => {
      // "If no weapon is present buy 1 weapon and add it to your hand." The
      // buy half is an ordinary buyOneCard (same player-choice fix as Clan
      // Champion's own Spoils above); the "if no weapon is present" gate
      // wraps it, so a hand that already holds a weapon still no-ops without
      // ever offering a picker.
      const buy = buyOneCard({
        matches: (def) => def.traits.includes('Weapon'),
        onEmpty: 'skip',
        buyOptions: { requireGoldCheck: false, destination: 'hand' },
      });
      const noWeaponPresent = (state, { playerId }) => !state.players.find((p) => p.id === playerId).hand
        .some((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
      return {
        getBuyableSources: (state, ctx) => (noWeaponPresent(state, ctx) ? buy.getBuyableSources(state, ctx) : []),
        resolve: (state, ctx) => (noWeaponPresent(state, ctx) ? buy.resolve(state, ctx) : state),
      };
    })(),
  ],
});

// --- Dark ---------------------------------------------------------------

function darkDestroyDiscardCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.discard.filter((id) => {
    const def = getInstanceDefinition(state, id);
    return isHeroLike(def) || def.traits.includes('Villager');
  });
}
register('hero-dark-l1', {
  // "Destroy 1 hero or villager in your discard pile to add Magic Attack +1."
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const targetId = choices?.instanceId ?? darkDestroyDiscardCandidates(state, { playerId })[0];
      if (!targetId) throw new Error('No hero or villager in your discard pile to destroy.');
      return grantMagicAttack(destroyFromDiscard(state, playerId, targetId), playerId, 1);
    },
    getCandidates: darkDestroyDiscardCandidates,
  }],
});
register('hero-dark-l2', {
  // "Add Magic Attack equal to the VP of 1 monster present." No "of your
  // choice" text and monster-type cards essentially never sit in hand under
  // normal play - auto-picks the first one found (same convention as every
  // other un-rulebooked "no explicit choice" pick in this pass).
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const monsterId = player.hand.find((id) => getInstanceDefinition(state, id).type === 'monster');
      if (!monsterId) return state;
      return grantMagicAttack(state, playerId, getInstanceDefinition(state, monsterId).points || 0);
    },
  }],
});
function darkTotalMonsterVp(state, playerId, { includeDiscardTop5 = false } = {}) {
  const player = state.players.find((p) => p.id === playerId);
  const pool = includeDiscardTop5 ? [...player.hand, ...player.discard.slice(-5)] : player.hand;
  return pool.reduce((sum, id) => {
    const def = getInstanceDefinition(state, id);
    return def.type === 'monster' ? sum + (def.points || 0) : sum;
  }, 0);
}
register('hero-dark-l3', {
  dungeon: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, darkTotalMonsterVp(state, playerId)) }],
});
register('hero-dark-l4', {
  // "...or in the top 5 cards of your discard pile" - an expanded matching
  // pool (hand + top 5 of discard combined), not "whichever is higher."
  // Discard's own "top" = the most recently pushed end (every discard push
  // in this engine appends), so the top 5 is discard.slice(-5).
  dungeon: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, darkTotalMonsterVp(state, playerId, { includeDiscardTop5: true })) }],
});

// --- Divine ---------------------------------------------------------------

function divineDiseaseCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Curse'));
}
function divineDestroyDiseaseToDraw(count) {
  return (state, { playerId, choices }) => {
    const targetId = choices?.instanceId ?? divineDiseaseCandidates(state, { playerId })[0];
    if (!targetId) throw new Error('No card with the Disease trait present to destroy.');
    return drawCards(destroyFromHand(state, playerId, targetId), playerId, count);
  };
}
// "Add Physical Attack +1 for each disease and monster present."
function divinePhysicalAttackPerMonsterAndDisease(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  const count = player.hand.filter((id) => {
    const def = getInstanceDefinition(state, id);
    return def.type === 'monster' || def.traits.includes('Curse');
  }).length;
  return count ? grantPhysicalAttack(state, playerId, count) : state;
}
function divineDrawPerMonster(multiplier) {
  return (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const count = player.hand.filter((id) => getInstanceDefinition(state, id).type === 'monster').length;
    return count ? drawCards(state, playerId, count * multiplier) : state;
  };
}
register('hero-divine-l1', {
  village: [{ resolve: divineDestroyDiseaseToDraw(1), getCandidates: divineDiseaseCandidates }],
  dungeon: [{ resolve: divineDestroyDiseaseToDraw(1), getCandidates: divineDiseaseCandidates }],
});
register('hero-divine-l2', {
  village: [{ resolve: (state, { playerId }) => drawCards(state, playerId, 1) }],
  dungeon: [
    { resolve: (state, { playerId }) => drawCards(state, playerId, 1) },
    { resolve: divinePhysicalAttackPerMonsterAndDisease },
  ],
});
register('hero-divine-l3', {
  village: [
    { resolve: divineDestroyDiseaseToDraw(2), getCandidates: divineDiseaseCandidates },
    { resolve: divineDrawPerMonster(1) },
  ],
  dungeon: [
    { resolve: divineDestroyDiseaseToDraw(2), getCandidates: divineDiseaseCandidates },
    { resolve: divineDrawPerMonster(1) },
  ],
});
register('hero-divine-l4', {
  village: [
    { resolve: divineDestroyDiseaseToDraw(2), getCandidates: divineDiseaseCandidates },
    { resolve: divineDrawPerMonster(1) },
  ],
  dungeon: [
    { resolve: divineDestroyDiseaseToDraw(2), getCandidates: divineDiseaseCandidates },
    { resolve: divineDrawPerMonster(2) },
  ],
});

// --- Gohlen -----------------------------------------------------------------

register('hero-gohlen-l1', {
  // "Add Physical Attack +2 if a monster is present." A binary bonus (not
  // scaled by count) - monster-type cards essentially never sit in hand
  // under normal play, correct as written even though it will rarely fire.
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const hasMonster = player.hand.some((id) => getInstanceDefinition(state, id).type === 'monster');
      return hasMonster ? grantPhysicalAttack(state, playerId, 2) : state;
    },
  }],
});
register('hero-gohlen-l2', {
  dungeon: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const hasMonster = player.hand.some((id) => getInstanceDefinition(state, id).type === 'monster');
      return hasMonster ? grantPhysicalAttack(state, playerId, 3) : state;
    },
  }],
});
// "Each other player discards 1 monster or shows a hand with none." No
// "of their choice" text and monster-type cards essentially never sit in
// hand - auto-picks the first one found per player, same "no explicit
// choice -> auto-pick" convention as every other un-rulebooked pick here.
function gohlenEachOtherPlayerDiscardsMonster(state, { playerId }) {
  let nextState = state;
  for (const p of state.players) {
    if (p.id === playerId) continue;
    const current = nextState.players.find((pp) => pp.id === p.id);
    const targetId = current.hand.find((id) => getInstanceDefinition(nextState, id).type === 'monster');
    if (targetId) nextState = discardFromHand(nextState, p.id, targetId);
  }
  return nextState;
}
// "Place 1 discarded monster of 5 VP or lower onto your discard pile [if it
// is from the same group as the monster you defeated]." Scans every
// player's discard pile (not just whatever this card's own Dungeon ability
// happened to discard this turn - the printed text says "1 discarded
// monster," not "a monster YOU just discarded") for the first match -
// excluding the just-defeated monster itself (already sitting in the
// winner's own discard as the normal trophy from this same fight, not a
// distinct "additional" copy to reclaim).
function gohlenReclaimDiscardedMonster(requireSameGroup) {
  return (state, { playerId }) => {
    const defeatedGroup = requireSameGroup ? state.meta.lastCombatResult?.monsterDef?.group : undefined;
    const defeatedInstanceId = state.meta.lastCombatResult?.monsterInstanceId;
    for (const p of state.players) {
      const targetId = p.discard.find((id) => {
        if (id === defeatedInstanceId) return false;
        const def = getInstanceDefinition(state, id);
        return def.type === 'monster' && (def.points || 0) <= 5 && (!requireSameGroup || def.group === defeatedGroup);
      });
      if (!targetId) continue;
      const withoutCard = { ...state, players: state.players.map((pp) => (pp.id === p.id ? { ...pp, discard: pp.discard.filter((id) => id !== targetId) } : pp)) };
      return { ...withoutCard, players: withoutCard.players.map((pp) => (pp.id === playerId ? { ...pp, discard: [...pp.discard, targetId] } : pp)) };
    }
    return state;
  };
}
register('hero-gohlen-l3', {
  dungeon: [{ resolve: gohlenEachOtherPlayerDiscardsMonster }],
  spoils: [{ resolve: gohlenReclaimDiscardedMonster(true) }],
});
register('hero-gohlen-l4', {
  dungeon: [{ resolve: gohlenEachOtherPlayerDiscardsMonster }],
  spoils: [{ resolve: gohlenReclaimDiscardedMonster(false) }],
});

// --- Harruli ------------------------------------------------------------

function harruliMagicPerSpell(amount) {
  return (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const spellCount = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Spell')).length;
    return spellCount ? grantMagicAttack(state, playerId, amount * spellCount) : state;
  };
}
register('hero-harruli-l1', { dungeon: [{ resolve: harruliMagicPerSpell(1) }] });
register('hero-harruli-l2', { dungeon: [{ resolve: harruliMagicPerSpell(2) }] });
// "Reveal the top N cards of your deck. Place [1/all] revealed spell(s) (if
// any) into your hand. Discard the rest." Revealed cards land in discard by
// default (revealTopOfOwnDeck's own convention) - matching spells are
// pulled back out into hand; everything else stays discarded.
function harruliRevealSpells(count, takeAll) {
  return (state, { playerId }) => {
    let nextState = state;
    const revealed = [];
    for (let i = 0; i < count; i += 1) {
      const { state: afterReveal, instanceId } = revealTopOfOwnDeck(nextState, playerId);
      nextState = afterReveal;
      if (instanceId) revealed.push(instanceId);
    }
    const spells = revealed.filter((id) => getInstanceDefinition(nextState, id).traits.includes('Spell'));
    const toHand = takeAll ? spells : spells.slice(0, 1);
    if (!toHand.length) return nextState;
    return {
      ...nextState,
      players: nextState.players.map((p) => (p.id === playerId
        ? { ...p, discard: p.discard.filter((id) => !toHand.includes(id)), hand: [...p.hand, ...toHand] }
        : p)),
    };
  };
}
register('hero-harruli-l3', { dungeon: [{ resolve: harruliRevealSpells(3, false) }] });
register('hero-harruli-l4', { dungeon: [{ resolve: harruliRevealSpells(4, true) }] });

// --- Isri ---------------------------------------------------------------

function isriDestroyToPhysicalAttackCandidates(matches) {
  return (state, { playerId }) => state.players.find((p) => p.id === playerId).hand.filter((id) => matches(getInstanceDefinition(state, id)));
}
// Isri Helmsplitter/Heartseeker (L3/L4): "...or destroy 1 weapon from the
// village [row]." Destroys (not buys) the top card of the cheapest matching
// stack - shares findCheapestBuyableSource's own stack lookup with the
// Spoils/buy-timed uses above, just applying a destroy instead of a
// purchase to whatever it finds.
function destroyTopOfCheapestStack(state, matches, chosenSource = null) {
  // chosenSource (choices.source, from PlayerDock's village-weapon picker) is
  // validated against the same legal-stack lookup the default path uses -
  // same stale-pick guard reasoning as buyOneCard.js - falling back to the
  // cheapest stack when absent or no longer legal.
  const legal = findAllBuyableSources(state, matches);
  const source = (chosenSource && legal.find((c) => c.type === chosenSource.type && c.key === chosenSource.key)) || legal[0];
  if (!source) return null;
  const stackField = source.type === 'village' ? 'villageStacks' : 'basicStacks';
  const stack = state.village[stackField][source.key];
  const topInstanceId = stack[0];
  const village = { ...state.village, [stackField]: { ...state.village[stackField], [source.key]: stack.slice(1) } };
  return { state: { ...state, village }, weight: getInstanceDefinition(state, topInstanceId).weight || 0 };
}
register('hero-isri-l1', {
  // "Destroy 1 item or weapon to add Physical Attack +1."
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const candidates = isriDestroyToPhysicalAttackCandidates((def) => def.traits.includes('Item') || def.traits.includes('Weapon'))(state, { playerId });
      const targetId = choices?.instanceId ?? candidates[0];
      if (!targetId) throw new Error('No item or weapon present to destroy.');
      return grantPhysicalAttack(destroyFromHand(state, playerId, targetId), playerId, 1);
    },
    getCandidates: isriDestroyToPhysicalAttackCandidates((def) => def.traits.includes('Item') || def.traits.includes('Weapon')),
  }],
});
register('hero-isri-l2', {
  // "Destroy 1 weapon to add Physical Attack equal to the weapon's Weight."
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      const candidates = isriDestroyToPhysicalAttackCandidates((def) => def.traits.includes('Weapon'))(state, { playerId });
      const targetId = choices?.instanceId ?? candidates[0];
      if (!targetId) throw new Error('No weapon present to destroy.');
      const weight = getInstanceDefinition(state, targetId).weight || 0;
      return grantPhysicalAttack(destroyFromHand(state, playerId, targetId), playerId, weight);
    },
    getCandidates: isriDestroyToPhysicalAttackCandidates((def) => def.traits.includes('Weapon')),
  }],
  // "Spoils: Buy 1 weapon." Matt: "I just tried to use the Isri
  // Bladespinner's spoils ability and the game just chose a weapon for me. I
  // should have been able to choose myself what weapon I wanted." Same
  // cheapest-weapon auto-pick Clan Champion had.
  spoils: [buyOneCard({
    matches: (def) => def.traits.includes('Weapon'),
    onEmpty: 'skip',
  })],
});
register('hero-isri-l3', {
  // "Destroy 1 weapon present or 1 weapon from the village to add Physical
  // Attack equal to the weapon's Weight." The village-row half reuses
  // findCheapestBuyableSource's own stack lookup, but destroys (not buys)
  // the top card - auto-picks a hand weapon first (a real card the player
  // already owns) before reaching into the village row, since the printed
  // order lists "present" before "from the village."
  // Matt reported the missing half directly: "The Isri Helmsplitter is
  // allowed to destroy a card that is present or in the village but it's
  // only letting me choose from my hand." The village branch used to be
  // reachable only with a weapon-less hand (and then auto-picked the
  // cheapest stack with no choice at all) - choices.source now selects a
  // specific village/basic weapon stack explicitly, taking priority over
  // the hand default. The hand-first fallback with no choices at all is
  // unchanged (AI/replays). No getCandidates registered any more -
  // PlayerDock's dedicated Isri branch runs the whole two-branch flow
  // (present-vs-village, then the matching picker) itself, same
  // hardcoded-branch convention as Trader's trade-up.
  dungeon: [{
    resolve: (state, { playerId, choices }) => {
      if (choices?.source) {
        const result = destroyTopOfCheapestStack(state, (def) => def.traits.includes('Weapon'), choices.source);
        if (!result) throw new Error('No weapon stack remains to destroy from.');
        return grantPhysicalAttack(result.state, playerId, result.weight);
      }
      const handCandidates = isriDestroyToPhysicalAttackCandidates((def) => def.traits.includes('Weapon'))(state, { playerId });
      const targetId = choices?.instanceId ?? handCandidates[0];
      if (targetId) {
        const weight = getInstanceDefinition(state, targetId).weight || 0;
        return grantPhysicalAttack(destroyFromHand(state, playerId, targetId), playerId, weight);
      }
      const result = destroyTopOfCheapestStack(state, (def) => def.traits.includes('Weapon'));
      return result ? grantPhysicalAttack(result.state, playerId, result.weight) : state;
    },
  }],
});
register('hero-isri-l4', {
  // "Add Physical Attack equal to the Weight of 1 weapon equipped to this
  // hero, or destroy 1 weapon from the village to add Physical Attack equal
  // to that weapon's Weight." Unlike L1-L3, this doesn't destroy the
  // equipped weapon - it stays equipped, this just reads its Weight.
  dungeon: [{
    // Same choices.source branch selection as L3 just above (and the same
    // PlayerDock Isri flow drives it) - here the "present" half reads an
    // equipped weapon's Weight without destroying anything.
    resolve: (state, { playerId, instanceId, choices }) => {
      if (choices?.source) {
        const result = destroyTopOfCheapestStack(state, (def) => def.traits.includes('Weapon'), choices.source);
        if (!result) throw new Error('No weapon stack remains to destroy from.');
        return grantPhysicalAttack(result.state, playerId, result.weight);
      }
      const player = state.players.find((p) => p.id === playerId);
      const equippedIds = getEquippedWeaponIds(player, instanceId);
      const equippedId = choices?.instanceId && equippedIds.includes(choices.instanceId) ? choices.instanceId : equippedIds[0];
      if (equippedId) return grantPhysicalAttack(state, playerId, getInstanceDefinition(state, equippedId).weight || 0);
      const result = destroyTopOfCheapestStack(state, (def) => def.traits.includes('Weapon'));
      return result ? grantPhysicalAttack(result.state, playerId, result.weight) : state;
    },
  }],
});

// --- Stoneguard -----------------------------------------------------------
// "specialEffects: Add Physical Attack +N while equipped. Add Physical
// Attack +M for each mercenary present." "While equipped" on a HERO card
// (not a weapon) most plausibly means "while THIS hero has a weapon
// equipped" - a documented reading, not confirmed against a rulebook, see
// docs/expansion-audit.md. "Mercenary" is a real printed trait on several
// Villager-type village cards (Guide, Trader, etc.), not a special equip
// slot - "present" means in hand, this engine's standing convention.

register('hero-stoneguard-l1', {}); // no printed ability beyond stats

register('hero-stoneguard-l3', {
  spoils: [{
    // "Add any mercenaries present to your new hand at the end of the turn."
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const mercIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Mercenary'));
      if (!mercIds.length) return state;
      return updatePlayer(state, playerId, (p) => ({
        ...p, turnFlags: { ...p.turnFlags, keptForNextHandInstanceIds: [...p.turnFlags.keptForNextHandInstanceIds, ...mercIds] },
      }));
    },
  }],
});
register('hero-stoneguard-l4', {
  spoils: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const mercIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Mercenary'));
      if (!mercIds.length) return state;
      return updatePlayer(state, playerId, (p) => ({
        ...p, turnFlags: { ...p.turnFlags, keptForNextHandInstanceIds: [...p.turnFlags.keptForNextHandInstanceIds, ...mercIds] },
      }));
    },
  }],
});

// --- Tholis ---------------------------------------------------------------

// "Look at the top N cards of the dungeon deck. Return them [to the top of
// the dungeon deck] in any order." choices.order (an array of the revealed
// instanceIds in the desired new order) - defaults to the order revealed
// (a no-op reorder) if not supplied, same "supply a fully-resolved choice"
// convention as other reorder abilities in this file.
function tholisLookAtTop(count) {
  return (state, { choices }) => {
    const dungeonDeck = [...state.dungeonDeck];
    const revealed = dungeonDeck.splice(0, Math.min(count, dungeonDeck.length));
    const order = choices?.order && choices.order.length === revealed.length ? choices.order : revealed;
    return { ...state, dungeonDeck: [...order, ...dungeonDeck] };
  };
}
register('hero-tholis-l1', { dungeon: [{ resolve: tholisLookAtTop(3) }] });
register('hero-tholis-l2', {
  // "...You may switch 1 of them with the monster in rank 1. Return the
  // cards to the top of the dungeon deck in any order." choices.swapWithRank1
  // (one of the 3 revealed instanceIds) opts into the swap; the displaced
  // rank-1 occupant joins the group of cards being returned to the deck.
  dungeon: [{
    resolve: (state, { choices }) => {
      const dungeonDeck = [...state.dungeonDeck];
      const revealed = dungeonDeck.splice(0, Math.min(3, dungeonDeck.length));
      let toReturn = choices?.order && choices.order.length === revealed.length ? [...choices.order] : [...revealed];
      const dungeonHall = [...state.dungeonHall];
      if (choices?.swapWithRank1 && toReturn.includes(choices.swapWithRank1) && dungeonHall[0]) {
        const rank1Id = dungeonHall[0];
        toReturn = toReturn.map((id) => (id === choices.swapWithRank1 ? rank1Id : id));
        dungeonHall[0] = choices.swapWithRank1;
      }
      return { ...state, dungeonHall, dungeonDeck: [...toReturn, ...dungeonDeck] };
    },
  }],
});
// "Each other player reveals the top 5 cards of his or her deck, discards
// any [heroes/cards with a gold cost], then returns the rest in any order."
// Revealed cards land in that player's own discard by default
// (revealTopOfOwnDeck's convention) - matches() stay there ("discarded");
// the rest move back to the top of that SAME player's own deck, in reveal
// order (no cross-player choice text, so no reordering choice offered).
function tholisEachOtherPlayerRevealDiscard(matches) {
  return (state, { playerId }) => {
    let nextState = state;
    for (const p of state.players) {
      if (p.id === playerId) continue;
      const revealed = [];
      for (let i = 0; i < 5; i += 1) {
        const { state: afterReveal, instanceId } = revealTopOfOwnDeck(nextState, p.id);
        nextState = afterReveal;
        if (instanceId) revealed.push(instanceId);
      }
      const toReturn = revealed.filter((id) => !matches(getInstanceDefinition(nextState, id)));
      nextState = {
        ...nextState,
        players: nextState.players.map((pp) => (pp.id === p.id
          ? { ...pp, discard: pp.discard.filter((id) => !toReturn.includes(id)), deck: [...toReturn, ...pp.deck] }
          : pp)),
      };
    }
    return nextState;
  };
}
register('hero-tholis-l3', { dungeon: [{ resolve: tholisEachOtherPlayerRevealDiscard((def) => isHeroLike(def)) }] });
register('hero-tholis-l4', { dungeon: [{ resolve: tholisEachOtherPlayerRevealDiscard((def) => (def.cost || 0) > 0) }] });

// --- Woodfolk -------------------------------------------------------------
// "specialEffects: Add Magic Attack +N against monsters that give
// diseases." combat/attackMath.js's contributesTraits (a per-card
// alwaysActive check against MONSTERS_THAT_GIVE_DISEASES - unlike Bolt of
// Purity's own disease-conditional bonus, this applies automatically just
// by being present, no Dungeon-ability activation needed).

register('hero-woodfolk-l1', {}); // specialEffects handled generically above
register('hero-woodfolk-l2', {
  dungeon: [{
    // "Destroy 1 disease to draw 1 card."
    resolve: (state, { playerId, choices }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidates = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Curse'));
      const targetId = choices?.instanceId ?? candidates[0];
      if (!targetId) throw new Error('No card with the Disease trait present to destroy.');
      return drawCards(destroyFromHand(state, playerId, targetId), playerId, 1);
    },
    getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Curse')),
  }],
});
function woodfolkDestroyDiseaseForMagicAndDraw(amount) {
  return (state, { playerId, choices }) => {
    const player = state.players.find((p) => p.id === playerId);
    const candidates = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Curse'));
    const targetId = choices?.instanceId ?? candidates[0];
    if (!targetId) throw new Error('No card with the Disease trait present to destroy.');
    return drawCards(grantMagicAttack(destroyFromHand(state, playerId, targetId), playerId, amount), playerId, 1);
  };
}
register('hero-woodfolk-l3', {
  dungeon: [{
    resolve: woodfolkDestroyDiseaseForMagicAndDraw(4),
    getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Curse')),
  }],
});
register('hero-woodfolk-l4', {
  dungeon: [{
    resolve: woodfolkDestroyDiseaseForMagicAndDraw(6),
    getCandidates: (state, { playerId }) => state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Curse')),
  }],
});
